import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Typography,
  TextField,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Card,
  CardContent,
  Grid,
  InputAdornment,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Alert,
  LinearProgress,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TablePagination,
  Autocomplete,
  ToggleButton,
  ToggleButtonGroup,
  Collapse,
} from '@mui/material';
import {
  ArrowBack,
  Receipt,
  AccountBalance,
  Description,
  Comment,
  MoneyOff,
  Timeline,
  CheckCircle,
  Save,
  Print,
  Download,
  Cancel as CancelIcon,
  Upload,
  Edit,
  Delete,
  CheckCircleOutline,
  WatchLater,
  Search,
  Groups,
  ReceiptLong,
  KeyboardArrowDown,
  KeyboardArrowUp,
} from '@mui/icons-material';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import {
  updateBooking,
  getBookingTransactions,
  getBookingById,
  createReceipt,
  deleteReceipt,
  getInstallmentPlan,
  createOrReplaceInstallmentPlan,
  updateInstallmentStatus,
  createBookingFollowUp,
  getBookingFollowUps,
  getBookingProgress,
  getBookingDocuments,
  getCommissionGenerator,
  getBookingHierarchy,
  getGroupedCommissionReports,
  updateBookingApprovedStatus,
} from '../../services/project';
import { getInquiryStatuses } from '../../services/master';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { getSignedUrls, uploadFileToS3, viewUrl } from '../../services/s3';
import { globalMasterService } from '../../services/stateAPI';
import { getAssociates } from '../../services/associate';
import BookingDetailsPDF from './BookingDetailsPDF';
import { hasWritePermission } from '../../utils/menuFilter';
import moment from 'moment';

const BookingDetails = () => {
  const history = useHistory();
  const canWriteBooking = hasWritePermission('booking.booking-details');
  const canViewCommission = hasWritePermission('booking.commission');
  const canViewFollowup = hasWritePermission('booking.followup');
  const canViewReceipt = hasWritePermission('booking.receipt');
  const canViewRefundReceipt = hasWritePermission('booking.refund-receipt');
  const canViewDocument = hasWritePermission('booking.document');
  const canViewProgress = hasWritePermission('booking.progress');
  const canViewStatus = hasWritePermission('booking.booking-status');
  const canCancelBooking =
    hasWritePermission('booking.cancel-plot-booking') ||
    hasWritePermission('plot-booking.cancel-plot-booking');
  const location = useLocation();
  const { id } = useParams();
  const [activeTab, setActiveTab] = useState(0);
  const [isEditMode, setIsEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [receipts, setReceipts] = useState([]);
  const [receiptsLoading, setReceiptsLoading] = useState(false);
  const [receiptsError, setReceiptsError] = useState('');
  const [bookingInfo, setBookingInfo] = useState(null);
  const [deletingReceiptId, setDeletingReceiptId] = useState(null);
  const [deleteReceiptDialogOpen, setDeleteReceiptDialogOpen] = useState(false);
  const [receiptToDelete, setReceiptToDelete] = useState(null);
  const [cancelRemark, setCancelRemark] = useState('');
  const [receiptForm, setReceiptForm] = useState({
    receivedDate: new Date().toISOString().split('T')[0],
    receivedType: '',
    paymentType: '',
    mode: '',
    bankId: '',
    refNo: '',
    bankName: '',
    receiverName: '',
    upiId: '',
    accountHolderName: '',
    accountNumber: '',
    ifscCode: '',
    cardLast4: '',
    amount: '',
    otherRemark: '',
  });
  const [savingReceipt, setSavingReceipt] = useState(false);
  const [receiptSaveError, setReceiptSaveError] = useState('');
  const [receiptSaveSuccess, setReceiptSaveSuccess] = useState(false);
  const [amountError, setAmountError] = useState('');
  const [banksList, setBanksList] = useState([]);
  const [banksLoading, setBanksLoading] = useState(false);
  const [installmentPlan, setInstallmentPlan] = useState({
    numberOfInstallments: '',
    installmentAmount: '',
    startDate: '',
    frequency: '',
  });
  const [existingInstallmentPlan, setExistingInstallmentPlan] = useState(null);
  const [installmentPlanLoading, setInstallmentPlanLoading] = useState(false);
  const [installmentPlanError, setInstallmentPlanError] = useState('');
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [statusDialogSaving, setStatusDialogSaving] = useState(false);
  const [statusDialogError, setStatusDialogError] = useState('');
  const [selectedInstallment, setSelectedInstallment] = useState(null);
  const [statusForm, setStatusForm] = useState({
    status: 'PAID',
    paidDate: new Date().toISOString().split('T')[0],
    remark: '',
  });
  const [followUps, setFollowUps] = useState([]);
  const [followUpsLoading, setFollowUpsLoading] = useState(false);
  const [followUpsError, setFollowUpsError] = useState('');
  const [followUpsPage, setFollowUpsPage] = useState(0);
  const [followUpsRowsPerPage, setFollowUpsRowsPerPage] = useState(10);
  const [followUpsTotalCount, setFollowUpsTotalCount] = useState(0);
  const [inquiryStatuses, setInquiryStatuses] = useState([]);
  const [followUpForm, setFollowUpForm] = useState({
    date: new Date().toISOString(),
    nextFollowUpDate: '',
    status: '',
    remark: '',
  });
  const [savingFollowUp, setSavingFollowUp] = useState(false);
  const [followUpSaveError, setFollowUpSaveError] = useState('');
  const [followUpSaveSuccess, setFollowUpSaveSuccess] = useState('');
  const [followUpPickerOpen, setFollowUpPickerOpen] = useState(false);
  const [nextFollowUpPickerOpen, setNextFollowUpPickerOpen] = useState(false);
  const [progressData, setProgressData] = useState([]);
  const [progressLoading, setProgressLoading] = useState(false);
  const [progressError, setProgressError] = useState('');
  const [commissions, setCommissions] = useState([]);
  const [commissionsLoading, setCommissionsLoading] = useState(false);
  const [commissionsError, setCommissionsError] = useState('');
  const [totalCommission, setTotalCommission] = useState(0);
  const [totalDistribution, setTotalDistribution] = useState(0);
  const [commissionsPage, setCommissionsPage] = useState(0);
  const [commissionsLimit, setCommissionsLimit] = useState(10);
  const [commissionsTotalCount, setCommissionsTotalCount] = useState(0);
  const [commissionsAssociateFilter, setCommissionsAssociateFilter] =
    useState(null);
  const [isCommissionTypeFixed, setIsCommissionTypeFixed] = useState(false);
  const [fixedCommissionRemark, setFixedCommissionRemark] = useState('');
  const [commissionViewMode, setCommissionViewMode] = useState('detailed'); // 'detailed' | 'grouped'
  const [expandedGroupedRows, setExpandedGroupedRows] = useState({});
  const [associatesList, setAssociatesList] = useState([]);
  const [associatesLoading, setAssociatesLoading] = useState(false);
  const [pdfDialogOpen, setPdfDialogOpen] = useState(false);
  const [pdfType, setPdfType] = useState('receipt');
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [documentsError, setDocumentsError] = useState('');
  const [documentUpload, setDocumentUpload] = useState({
    type: '',
    file: null,
    name: '',
  });
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [documentUploadError, setDocumentUploadError] = useState('');
  const [documentUploadSuccess, setDocumentUploadSuccess] = useState(false);
  const [fetchedBooking, setFetchedBooking] = useState(null);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [bookingError, setBookingError] = useState('');
  const [statusUpdateForm, setStatusUpdateForm] = useState({
    approvedStatus: 'registry',
    remark: '',
    date: new Date().toISOString().split('T')[0],
  });
  const [statusUpdateSaving, setStatusUpdateSaving] = useState(false);
  const [statusUpdateError, setStatusUpdateError] = useState('');
  const [statusUpdateSuccess, setStatusUpdateSuccess] = useState('');

  const availableTabs = [
    { label: 'Booking', show: true },
    { label: 'Installment', show: true },
    { label: 'Receipt', show: canViewReceipt },
    { label: 'Document', show: canViewDocument },
    { label: 'Followup/Remark', show: canViewFollowup },
    { label: 'Commission', show: canViewCommission },
    { label: 'Progress', show: canViewProgress },
    { label: 'Booking Status', show: canViewStatus },
  ].filter((t) => t.show);

  const activeTabLabel = availableTabs[activeTab]?.label;

  const handleUpdateBookingStatus = async () => {
    if (!id) return;
    setStatusUpdateSaving(true);
    setStatusUpdateError('');
    setStatusUpdateSuccess('');
    try {
      const response = await updateBookingApprovedStatus(id, statusUpdateForm);
      if (response.success) {
        setStatusUpdateSuccess(
          response.message || 'Status updated successfully!',
        );
        // Refresh booking details
        fetchBookingDetails();
      } else {
        setStatusUpdateError(response.message || 'Failed to update status');
      }
    } catch (err) {
      console.error('Error updating status:', err);
      setStatusUpdateError(
        err.message || 'Error occurred while updating status',
      );
    } finally {
      setStatusUpdateSaving(false);
    }
  };

  // Get booking data from location state, or use defaults
  const passedBooking = location.state?.bookingData || null;
  const originalData = fetchedBooking || passedBooking?._original || null;

  // Map the booking data - prioritize originalData (API response) over passedBooking (mapped data)
  const booking =
    originalData || passedBooking
      ? {
        // IDs and basic info
        id: originalData?._id || passedBooking?.id || id,
        bookingId: originalData?.bookingId
          ? String(originalData.bookingId)
          : passedBooking?.bookingId || id,
        customerId:
          originalData?.customerId || passedBooking?.customerId || '',
        customerNo:
          originalData?.customerNo || passedBooking?.customerNo || '',

        // Dates
        bookingDate:
          originalData?.bookingDate || passedBooking?.plotBookingDate || '',
        plotBookingDate: originalData?.bookingDate
          ? new Date(originalData.bookingDate).toISOString().split('T')[0]
          : passedBooking?.plotBookingDate || '',
        createdOn: originalData?.createdOn || '',
        createdAt: originalData?.createdAt || '',
        updatedAt: originalData?.updatedAt || '',

        // Status
        status:
          originalData?.approvedStatus || passedBooking?.status || 'pending',
        approvedStatus:
          originalData?.approvedStatus || passedBooking?.status || 'pending',

        // Financial fields - prioritize originalData
        propertyAmount:
          originalData?.propertyAmount ?? passedBooking?.plotAmount ?? 0,
        bookingAmount: originalData?.bookingAmount ?? 0,
        discount:
          originalData?.discount ??
          originalData?.companyDiscount ??
          passedBooking?.discountAmount ??
          0,
        totalReceived:
          originalData?.totalReceived ?? passedBooking?.paidAmount ?? 0,
        totalDueAmount: originalData?.totalDueAmount ?? 0,
        totalAuthorityCharge:
          originalData?.totalAuthorityCharge ??
          passedBooking?.totalAuthorityCharge ??
          0,

        // Legacy field mappings for backward compatibility
        plotAmount:
          originalData?.propertyAmount ?? passedBooking?.plotAmount ?? 0,
        discountAmount:
          originalData?.discount ??
          originalData?.companyDiscount ??
          passedBooking?.discountAmount ??
          0,
        paidAmount:
          originalData?.totalReceived ?? passedBooking?.paidAmount ?? 0,

        // Payment and other details
        paymentPlan:
          originalData?.paymentPlan || passedBooking?.paymentPlan || '',
        remark: originalData?.remark || passedBooking?.remark || '',
        mode: originalData?.mode || passedBooking?.mode || '',
        createdBy: originalData?.createdBy || '',
        updatedBy: originalData?.updatedBy || '',

        // Customer details - extract from nested object
        customer:
          originalData?.customer?.name || passedBooking?.customer || '',
        address:
          originalData?.customer?.address ||
          passedBooking?.address ||
          originalData?.customer?.otherDetails?.address ||
          '',
        customerDetails:
          originalData?.customer || passedBooking?.customerDetails || {},

        // Particular/Property details - extract from nested object
        plotNo:
          originalData?.particular?.inventoryDetail ||
          passedBooking?.plotNo ||
          '',
        inventoryType:
          originalData?.particular?.inventoryType ||
          passedBooking?.inventoryType ||
          '',
        branchName:
          originalData?.particular?.projectName ||
          passedBooking?.branchName ||
          '',
        particular:
          originalData?.particular || passedBooking?.particular || {},

        // Associate details - extract from nested object
        associate:
          originalData?.associate?.name || passedBooking?.associate || '',
        associateDetails:
          originalData?.associate || passedBooking?.associateDetails || {},

        // New fields from API response
        inventoryDetails:
          originalData?.inventoryDetails ||
          passedBooking?.inventoryDetails ||
          {},
        approvedDate:
          originalData?.approvedDate || passedBooking?.approvedDate || '',

        // Original full data
        _original: originalData || passedBooking?._original || null,
      }
      : {
        // Fallback mock data if no data passed
        id: id,
        bookingId: id || 'BK001',
        plotBookingDate: '2025-10-01',
        plotNo: 'P101',
        branchName: 'Mumbai',
        paymentPlan: 'EMI',
        customer: 'John Doe',
        associate: 'Agent A',
        plotAmount: 500000,
        discountAmount: 25000,
        paidAmount: 100000,
        status: 'booked',
      };

  const [customerDetails, setCustomerDetails] = useState({});

  // State for editable financial fields
  const [financialFields, setFinancialFields] = useState({
    propertyAmount: booking.propertyAmount ?? 0,
    bookingAmount: booking.bookingAmount ?? 0,
    discount: booking.discount ?? 0,
    totalReceived: booking.totalReceived ?? 0,
    totalDueAmount: booking.totalDueAmount ?? 0,
    plotAmount: booking.plotAmount ?? booking.propertyAmount ?? 0,
    totalAuthorityCharge: booking.totalAuthorityCharge ?? 0,
  });

  // Fetch booking by ID
  const fetchBookingDetails = useCallback(async () => {
    if (!id) return;
    try {
      setBookingLoading(true);
      setBookingError('');
      const res = await getBookingById(id);
      if (res.success && Array.isArray(res.data) && res.data.length > 0) {
        setFetchedBooking(res.data[0]);
      } else if (res.success && res.data) {
        setFetchedBooking(res.data);
      }
    } catch (err) {
      console.error('Error fetching booking details:', err);
      setBookingError(err.message || 'Failed to fetch booking details');
    } finally {
      setBookingLoading(false);
    }
  }, [id]);

  // Reset tab-specific states when booking changes
  useEffect(() => {
    setAssociatesList([]);
    setCommissionsAssociateFilter(null);
    setCommissionsPage(0);
  }, [id]);

  // Initial fetch and on Tab 0 change
  useEffect(() => {
    if (activeTabLabel === 'Booking') {
      fetchBookingDetails();
    }
  }, [activeTabLabel, fetchBookingDetails]);

  // On first load if no data passed via location
  useEffect(() => {
    if (!passedBooking || !passedBooking._original) {
      fetchBookingDetails();
    }
  }, [passedBooking, fetchBookingDetails]);

  // Update financial fields when booking data changes
  useEffect(() => {
    setFinancialFields({
      propertyAmount: booking.propertyAmount ?? 0,
      bookingAmount: booking.bookingAmount ?? 0,
      discount: booking.discount ?? 0,
      totalReceived: booking.totalReceived ?? 0,
      totalDueAmount: booking.totalDueAmount ?? 0,
      plotAmount: booking.plotAmount ?? booking.propertyAmount ?? 0,
      totalAuthorityCharge: booking.totalAuthorityCharge ?? 0,
    });
  }, [
    booking.propertyAmount,
    booking.bookingAmount,
    booking.discount,
    booking.totalReceived,
    booking.totalDueAmount,
    booking.plotAmount,
    booking.totalAuthorityCharge,
  ]);

  // Load banks for receipt dropdown
  useEffect(() => {
    const loadBanks = async () => {
      try {
        setBanksLoading(true);
        const banks = await globalMasterService.getBanks();
        setBanksList(Array.isArray(banks) ? banks : []);
      } catch (err) {
        console.error('Error fetching banks:', err);
        setBanksList([]);
      } finally {
        setBanksLoading(false);
      }
    };

    loadBanks();
  }, []);

  // Fetch receipts when Receipt tab is opened
  useEffect(() => {
    const fetchReceipts = async () => {
      if (activeTabLabel === 'Receipt') {
        // Use _id from original data, or fallback to id field
        const bookingId = originalData?._id || booking.id || booking._id;
        if (!bookingId) {
          setReceiptsError('Booking ID not found');
          setReceipts([]);
          setBookingInfo(null);
          return;
        }

        setReceiptsLoading(true);
        setReceiptsError('');
        try {
          const response = await getBookingTransactions(bookingId);
          if (response.success && Array.isArray(response.data)) {
            setReceipts(response.data);
            // Store bookingInfo from API response
            if (response.bookingInfo) {
              setBookingInfo(response.bookingInfo);
            }
          } else {
            setReceiptsError('Invalid response format from server');
            setReceipts([]);
            setBookingInfo(null);
          }
        } catch (err) {
          console.error('Error fetching receipts:', err);
          setReceiptsError(err.message || 'Failed to load receipts');
          setReceipts([]);
          setBookingInfo(null);
        } finally {
          setReceiptsLoading(false);
        }
      }
    };

    fetchReceipts();
  }, [activeTab, originalData, booking.id]);

  // Fetch associates hierarchy for this booking when Commission tab is opened
  const fetchAssociates = useCallback(async () => {
    const bookingId = originalData?._id || booking.id || booking._id || id;
    if (!bookingId) return;

    try {
      setAssociatesLoading(true);
      const response = await getBookingHierarchy(bookingId);
      if (
        response.success &&
        response.data
      ) {
        if (Array.isArray(response.data.associates)) {
          setAssociatesList(response.data.associates);
        }
        if (response.data.booking?.bookingCommissionTypeFixed !== undefined) {
          setIsCommissionTypeFixed(Boolean(response.data.booking.bookingCommissionTypeFixed));
        }
        if (response.data.booking?.fixedCommissionRemark) {
          setFixedCommissionRemark(String(response.data.booking.fixedCommissionRemark));
        }
      } else {
        // Fallback or clear if no hierarchy found
        setAssociatesList([]);
      }
    } catch (err) {
      console.error('Failed to fetch booking hierarchy:', err);
      setAssociatesList([]);
    } finally {
      setAssociatesLoading(false);
    }
  }, [originalData?._id, booking.id, booking._id, id]);

  const fetchCommissions = useCallback(async () => {
    if (activeTabLabel !== 'Commission') return;
    const bookingId = originalData?._id || booking.id || booking._id || id;
    if (!bookingId) {
      setCommissions([]);
      setTotalCommission(0);
      setTotalDistribution(0);
      setCommissionsError('Booking ID not found');
      return;
    }

    try {
      setCommissionsLoading(true);
      setCommissionsError('');

      let response;
      if (commissionViewMode === 'grouped') {
        response = await getGroupedCommissionReports({
          bookingId,
          page: commissionsPage + 1,
          limit: commissionsLimit,
          associateId: commissionsAssociateFilter?._id || '',
        });
      } else {
        response = await getCommissionGenerator({
          bookingId,
          page: commissionsPage + 1,
          limit: commissionsLimit,
          associateId: commissionsAssociateFilter?._id || '',
        });
      }

      if (response.success) {
        const list = Array.isArray(response.data) ? response.data : [];
        setCommissions(list);
        setTotalCommission(response.totalPayableCommissionAmount || 0);
        setTotalDistribution(response.totalDistributionAmount || 0);
        setCommissionsTotalCount(
          response.totalCount || response.count || list.length,
        );

        // Extract fixedCommissionRemark if present in API response
        const summary = response.bookingCommissionSummary || response.data?.bookingCommissionSummary || {};
        const remarkFromApi = response.fixedCommissionRemark || summary.fixedCommissionRemark || (response.data && !Array.isArray(response.data) ? response.data.fixedCommissionRemark : '') || originalData?.fixedCommissionRemark || '';
        const fixedTypeFromApi = response.bookingCommissionTypeFixed ?? summary.bookingCommissionTypeFixed ?? response.data?.bookingCommissionTypeFixed ?? originalData?.bookingCommissionTypeFixed;

        if (remarkFromApi) {
          setFixedCommissionRemark(String(remarkFromApi));
        }
        if (fixedTypeFromApi !== undefined) {
          setIsCommissionTypeFixed(Boolean(fixedTypeFromApi));
        }
      } else {
        throw new Error(response.message || 'Failed to load commissions');
      }
    } catch (err) {
      setCommissions([]);
      setTotalCommission(0);
      setTotalDistribution(0);
      setCommissionsError(err.message || 'Failed to load commissions');
    } finally {
      setCommissionsLoading(false);
    }
  }, [
    activeTabLabel,
    commissionViewMode,
    commissionsPage,
    commissionsLimit,
    commissionsAssociateFilter,
    originalData,
    booking.id,
    booking._id,
    id,
  ]);

  useEffect(() => {
    if (activeTabLabel === 'Commission') {
      fetchAssociates();
    }
  }, [activeTabLabel, fetchAssociates]);

  // Fetch commissions when tab or filter changes
  useEffect(() => {
    if (activeTabLabel === 'Commission') {
      fetchCommissions();
    }
  }, [activeTabLabel, fetchCommissions]);

  const handleChangePage = (event, newPage) => {
    setCommissionsPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setCommissionsLimit(parseInt(event.target.value, 10));
    setCommissionsPage(0);
  };

  const handleBack = () => {
    history.goBack();
  };

  const handleFinancialFieldChange = (field, value) => {
    setFinancialFields((prev) => ({
      ...prev,
      [field]: parseFloat(value) || 0,
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Get current user info for updatedBy
      let currentUserName = 'Admin User';
      try {
        const user = localStorage.getItem('user');
        if (user) {
          const userData = JSON.parse(user);
          currentUserName =
            userData?.user?.name ||
            userData?.name ||
            userData?.username ||
            'Admin User';
        }
      } catch (err) {
        console.error('Error getting user info:', err);
      }

      // Prepare payload with editable fields
      const payload = {
        propertyAmount: financialFields.propertyAmount,
        bookingAmount: financialFields.bookingAmount,
        discount: financialFields.discount,
        totalReceived: financialFields.totalReceived,
        totalDueAmount: financialFields.totalDueAmount,
        updatedBy: currentUserName,
      };

      // Get booking ID from booking object (use _id from original data)
      const bookingId = originalData?._id || booking.id || booking._id || id;

      if (!bookingId) {
        throw new Error('Booking ID is required');
      }

      // Call update API
      await updateBooking(bookingId, payload);

      // If no existing installment plan and user has configured a new plan, create it
      if (!existingInstallmentPlan && installmentSchedule.length > 0) {
        const totalDue = getTotalDue();
        const installmentsPayload = {
          bookingId: bookingId,
          totalDue: totalDue,
          numberOfInstallments: installmentSchedule.length,
          installmentAmount: parseFloat(installmentPlan.installmentAmount) || 0,
          startDate: installmentPlan.startDate, // YYYY-MM-DD
          frequency: installmentPlan.frequency,
          schedule: installmentSchedule.map((inst) => ({
            srNo: inst.srNo,
            dueDate: inst.dueDate, // YYYY-MM-DD as per requirement
            amount: inst.amount,
          })),
          createdBy: currentUserName,
        };

        const json = await createOrReplaceInstallmentPlan(installmentsPayload);

        // Store the created plan so UI switches to read-only mode
        if (json?.data) {
          setExistingInstallmentPlan(json.data);
        }

        alert('Booking and installment plan saved successfully!');
      } else {
        alert('Booking updated successfully!');
      }

      setIsEditMode(false);

      // Optionally refresh the page or update the booking object
      // You might want to reload the booking data here
    } catch (error) {
      console.error('Error updating booking:', error);
      alert(`Failed to update booking: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleReceiptFormChange = (field, value) => {
    if (field === 'mode') {
      setReceiptForm((prev) => ({
        ...prev,
        mode: value,
        refNo: '',
        bankName: '',
        bankId: '',
        receiverName: '',
        upiId: '',
        accountHolderName: '',
        accountNumber: '',
        ifscCode: '',
        cardLast4: '',
      }));
    } else {
      setReceiptForm((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
    // Clear errors when user starts typing
    if (receiptSaveError) setReceiptSaveError('');
    if (receiptSaveSuccess) setReceiptSaveSuccess(false);

    // Validate amount against total due
    if (field === 'amount') {
      const amount = parseFloat(value) || 0;
      // Calculate total due amount
      const totalDue =
        bookingInfo?.totalDueAmount ??
        booking.totalDueAmount ??
        Math.max(
          0,
          (bookingInfo?.propertyAmount ?? booking.propertyAmount ?? 0) -
          (bookingInfo?.totalReceived ?? booking.totalReceived ?? 0),
        );

      if (amount > totalDue) {
        setAmountError(
          `Amount cannot exceed total due amount of ₹${totalDue.toLocaleString('en-IN')}`,
        );
      } else {
        setAmountError('');
      }
    } else {
      // Clear amount error when other fields change
      if (amountError) setAmountError('');
    }
  };

  const handleBankSelect = (bankId) => {
    const selected = banksList.find((b) => b.id === bankId);
    setReceiptForm((prev) => ({
      ...prev,
      bankId: bankId,
      bankName: selected?.bankName || '',
    }));
    if (receiptSaveError) setReceiptSaveError('');
  };

  const getReceiptRequiredFields = (mode) => {
    switch (mode) {
      case 'Cash':
        return ['receiverName'];
      case 'Upi':
        return ['receiverName', 'upiId'];
      case 'RTGS/NEFT':
      case 'dd':
        return ['bankName'];
      case 'Cheque':
        return ['receiverName', 'bankName', 'refNo'];
      case 'card':
        return ['receiverName', 'bankName'];
      default:
        return [];
    }
  };

  const validateReceiptPaymentFields = () => {
    const req = getReceiptRequiredFields(receiptForm.mode);
    const missing = [];
    req.forEach((field) => {
      const val = receiptForm[field];
      if (!val || String(val).trim() === '') {
        missing.push(field);
      }
    });
    return missing;
  };

  const handleSaveReceipt = async () => {
    // Get booking ID from original data or booking object
    const bookingId = originalData?._id || booking.id || booking._id || id;

    if (!bookingId) {
      setReceiptSaveError('Booking ID not found');
      return;
    }

    // Validate required fields
    if (!receiptForm.receivedDate) {
      setReceiptSaveError('Date is required');
      return;
    }
    if (!receiptForm.receivedType) {
      setReceiptSaveError('Receipt Type is required');
      return;
    }
    if (!receiptForm.paymentType) {
      setReceiptSaveError('Payment Type is required');
      return;
    }
    if (!receiptForm.mode) {
      setReceiptSaveError('Payment Mode is required');
      return;
    }
    const missingPaymentFields = validateReceiptPaymentFields();
    if (missingPaymentFields.length) {
      setReceiptSaveError(
        'Please fill required payment details for selected mode.',
      );
      return;
    }
    if (!receiptForm.amount || parseFloat(receiptForm.amount) <= 0) {
      setReceiptSaveError('Amount is required and must be greater than 0');
      return;
    }

    // Validate amount against total due
    const amount = parseFloat(receiptForm.amount);
    const totalDue =
      bookingInfo?.totalDueAmount ??
      booking.totalDueAmount ??
      Math.max(
        0,
        (bookingInfo?.propertyAmount ?? booking.propertyAmount ?? 0) -
        (bookingInfo?.totalReceived ?? booking.totalReceived ?? 0),
      );

    if (amount > totalDue) {
      setReceiptSaveError(
        `Amount cannot exceed total due amount of ₹${totalDue.toLocaleString('en-IN')}`,
      );
      setAmountError(
        `Amount cannot exceed total due amount of ₹${totalDue.toLocaleString('en-IN')}`,
      );
      return;
    }

    setSavingReceipt(true);
    setReceiptSaveError('');
    setReceiptSaveSuccess(false);
    setAmountError('');

    try {
      // Get current user info for createdBy and updatedBy
      let currentUserName = 'Admin User';
      try {
        const user = localStorage.getItem('user');
        if (user) {
          const userData = JSON.parse(user);
          currentUserName =
            userData?.user?.name ||
            userData?.name ||
            userData?.username ||
            'Admin User';
        }
      } catch (err) {
        console.error('Error getting user info:', err);
      }

      // Map form values to API format
      const receivedTypeMap = {
        booking: 'Booking_Amount',
        installment: 'Installment',
        other: 'Other Charges',
        penalty: 'Penalty',
      };

      const paymentTypeMap = {
        full: 'fullPayment',
        partial: 'partialPayment',
        advance: 'Advance',
      };

      const payload = {
        bookingId: bookingId,
        amount: parseFloat(receiptForm.amount),
        receivedDate: receiptForm.receivedDate,
        receivedType:
          receivedTypeMap[receiptForm.receivedType] || receiptForm.receivedType,
        paymentType:
          paymentTypeMap[receiptForm.paymentType] || receiptForm.paymentType,
        mode: receiptForm.mode,
        refNo: receiptForm.refNo || '',
        bankId: receiptForm.bankId || '',
        bankName: receiptForm.bankName || '',
        receiverName: receiptForm.receiverName || '',
        upiId: receiptForm.upiId || '',
        accountHolderName: receiptForm.accountHolderName || '',
        accountNumber: receiptForm.accountNumber || '',
        ifscCode: receiptForm.ifscCode || '',
        cardLast4: receiptForm.cardLast4 || '',
        receiptStatus: 'pending',
        otherRemark: receiptForm.otherRemark || '',
        createdBy: currentUserName,
        updatedBy: currentUserName,
      };

      const response = await createReceipt(payload);

      if (response.success) {
        setReceiptSaveSuccess(true);
        // Reset form
        setReceiptForm({
          receivedDate: new Date().toISOString().split('T')[0],
          receivedType: '',
          paymentType: '',
          mode: '',
          bankId: '',
          refNo: '',
          bankName: '',
          receiverName: '',
          upiId: '',
          accountHolderName: '',
          accountNumber: '',
          ifscCode: '',
          cardLast4: '',
          amount: '',
          otherRemark: '',
        });
        setAmountError('');

        // Refresh receipts list
        const fetchReceipts = async () => {
          try {
            const receiptResponse = await getBookingTransactions(bookingId);
            if (
              receiptResponse.success &&
              Array.isArray(receiptResponse.data)
            ) {
              setReceipts(receiptResponse.data);
              if (receiptResponse.bookingInfo) {
                setBookingInfo(receiptResponse.bookingInfo);
              }
            }
          } catch (err) {
            console.error('Error refreshing receipts:', err);
          }
        };
        fetchReceipts();

        // Clear success message after 3 seconds
        setTimeout(() => setReceiptSaveSuccess(false), 3000);
      } else {
        throw new Error(response.message || 'Failed to create receipt');
      }
    } catch (error) {
      console.error('Error saving receipt:', error);
      setReceiptSaveError(error.message || 'Failed to save receipt');
    } finally {
      setSavingReceipt(false);
    }
  };

  const handleDeleteReceipt = (receipt) => {
    setReceiptToDelete(receipt);
    setCancelRemark('');
    setDeleteReceiptDialogOpen(true);
  };

  const handleConfirmDeleteReceipt = async () => {
    if (!receiptToDelete || !receiptToDelete._id) {
      setReceiptsError('Receipt ID not found');
      setDeleteReceiptDialogOpen(false);
      setReceiptToDelete(null);
      return;
    }

    const receiptId = receiptToDelete._id;
    const bookingId = originalData?._id || booking.id || booking._id || id;

    setDeletingReceiptId(receiptId);
    setReceiptsError('');

    try {
      await deleteReceipt(receiptId, { cancelRemark: cancelRemark.trim() });

      // Refresh receipts list after successful delete
      const fetchReceipts = async () => {
        try {
          const receiptResponse = await getBookingTransactions(bookingId);
          if (receiptResponse.success && Array.isArray(receiptResponse.data)) {
            setReceipts(receiptResponse.data);
            if (receiptResponse.bookingInfo) {
              setBookingInfo(receiptResponse.bookingInfo);
            }
          }
        } catch (err) {
          console.error('Error refreshing receipts:', err);
          setReceiptsError(err.message || 'Failed to refresh receipts');
        }
      };
      await fetchReceipts();

      setDeleteReceiptDialogOpen(false);
      setReceiptToDelete(null);
    } catch (error) {
      console.error('Error deleting receipt:', error);
      setReceiptsError(error.message || 'Failed to delete receipt');
    } finally {
      setDeletingReceiptId(null);
    }
  };

  const handleCancelDeleteReceipt = () => {
    setDeleteReceiptDialogOpen(false);
    setReceiptToDelete(null);
  };

  const getPaymentProgress = (paid, total) => {
    if (!total || total === 0) return 0;
    return (paid / total) * 100;
  };

  // Format currency for display
  const formatCurrency = (amount) => {
    return `₹${(amount ?? 0).toLocaleString('en-IN')}`;
  };

  // Calculate total due amount
  const getTotalDue = () => {
    return (
      bookingInfo?.totalDueAmount ??
      booking.totalDueAmount ??
      Math.max(
        0,
        (bookingInfo?.propertyAmount ?? booking.propertyAmount ?? 0) -
        (bookingInfo?.totalReceived ?? booking.totalReceived ?? 0),
      )
    );
  };

  // Calculate installment amount based on total due and number of installments
  const calculateInstallmentAmount = (totalDue, numberOfInstallments) => {
    if (
      !numberOfInstallments ||
      numberOfInstallments <= 0 ||
      !totalDue ||
      totalDue <= 0
    ) {
      return 0;
    }
    return Math.ceil(totalDue / numberOfInstallments);
  };

  const renderPaymentModeFields = () => {
    const commonProps = {
      fullWidth: true,
    };
    switch (receiptForm.mode) {
      case 'Cash':
        return (
          <Grid item xs={12} md={6} lg={4}>
            <TextField
              {...commonProps}
              label='Receiver Name'
              value={receiptForm.receiverName}
              onChange={(e) =>
                handleReceiptFormChange('receiverName', e.target.value)
              }
              required
            />
          </Grid>
        );
      case 'Upi':
        return (
          <>
            <Grid item xs={12} md={6} lg={4}>
              <TextField
                {...commonProps}
                label='Receiver Name'
                value={receiptForm.receiverName}
                onChange={(e) =>
                  handleReceiptFormChange('receiverName', e.target.value)
                }
                required
              />
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <TextField
                {...commonProps}
                label='UPI ID'
                value={receiptForm.upiId}
                onChange={(e) =>
                  handleReceiptFormChange('upiId', e.target.value)
                }
                required
              />
            </Grid>

            <Grid item xs={12} md={6} lg={4}>
              <FormControl fullWidth>
                <InputLabel>Bank Name</InputLabel>
                <Select
                  label='Bank Name'
                  value={receiptForm.bankId || ''}
                  onChange={(e) => handleBankSelect(e.target.value)}
                  disabled={banksLoading}
                >
                  <MenuItem value=''>
                    {banksLoading ? 'Loading...' : 'Select Bank'}
                  </MenuItem>
                  {banksList.map((bank) => (
                    <MenuItem key={bank.id} value={bank.id}>
                      {bank.bankName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </>
        );
      case 'RTGS/NEFT':
      case 'dd':
        return (
          <>
            {/* <Grid item xs={12} md={6} lg={4}>
              <TextField
                {...commonProps}
                label="Account Holder Name"
                value={receiptForm.accountHolderName}
                onChange={(e) => handleReceiptFormChange('accountHolderName', e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <TextField
                {...commonProps}
                label="Account Number"
                value={receiptForm.accountNumber}
                onChange={(e) => handleReceiptFormChange('accountNumber', e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <TextField
                {...commonProps}
                label="IFSC Code"
                value={receiptForm.ifscCode}
                onChange={(e) => handleReceiptFormChange('ifscCode', e.target.value)}
                required
              />
            </Grid> */}

            <Grid item xs={12} md={6} lg={4}>
              <FormControl fullWidth>
                <InputLabel>Bank Name*</InputLabel>
                <Select
                  label='Bank Name*'
                  value={receiptForm.bankId || ''}
                  onChange={(e) => handleBankSelect(e.target.value)}
                  disabled={banksLoading}
                >
                  <MenuItem value=''>
                    {banksLoading ? 'Loading...' : 'Select Bank'}
                  </MenuItem>
                  {banksList.map((bank) => (
                    <MenuItem key={bank.id} value={bank.id}>
                      {bank.bankName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </>
        );
      case 'Cheque':
        return (
          <>
            <Grid item xs={12} md={6} lg={4}>
              <TextField
                {...commonProps}
                label='Receiver Name'
                value={receiptForm.receiverName}
                onChange={(e) =>
                  handleReceiptFormChange('receiverName', e.target.value)
                }
                required
              />
            </Grid>

            <Grid item xs={12} md={6} lg={4}>
              <FormControl fullWidth>
                <InputLabel>Bank Name*</InputLabel>
                <Select
                  label='Bank Name*'
                  value={receiptForm.bankId || ''}
                  onChange={(e) => handleBankSelect(e.target.value)}
                  disabled={banksLoading}
                >
                  <MenuItem value=''>
                    {banksLoading ? 'Loading...' : 'Select Bank'}
                  </MenuItem>
                  {banksList.map((bank) => (
                    <MenuItem key={bank.id} value={bank.id}>
                      {bank.bankName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {/* <Grid item xs={12} md={6} lg={4}>
              <TextField
                {...commonProps}
                label="Account Holder Name"
                value={receiptForm.accountHolderName}
                onChange={(e) => handleReceiptFormChange('accountHolderName', e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <TextField
                {...commonProps}
                label="Account Number"
                value={receiptForm.accountNumber}
                onChange={(e) => handleReceiptFormChange('accountNumber', e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <TextField
                {...commonProps}
                label="IFSC Code"
                value={receiptForm.ifscCode}
                onChange={(e) => handleReceiptFormChange('ifscCode', e.target.value)}
                required
              />
            </Grid> */}
          </>
        );
      case 'card':
        return (
          <>
            <Grid item xs={12} md={6} lg={4}>
              <TextField
                {...commonProps}
                label='Card Last 4 Digits'
                value={receiptForm.cardLast4}
                onChange={(e) =>
                  handleReceiptFormChange('cardLast4', e.target.value)
                }
                inputProps={{ maxLength: 4 }}
                required
              />
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <TextField
                {...commonProps}
                label='Card Holder Name'
                value={receiptForm.receiverName}
                onChange={(e) =>
                  handleReceiptFormChange('receiverName', e.target.value)
                }
                required
              />
            </Grid>

            <Grid item xs={12} md={6} lg={4}>
              <FormControl fullWidth>
                <InputLabel>Bank Name*</InputLabel>
                <Select
                  label='Bank Name*'
                  value={receiptForm.bankId || ''}
                  onChange={(e) => handleBankSelect(e.target.value)}
                  disabled={banksLoading}
                >
                  <MenuItem value=''>
                    {banksLoading ? 'Loading...' : 'Select Bank'}
                  </MenuItem>
                  {banksList.map((bank) => (
                    <MenuItem key={bank.id} value={bank.id}>
                      {bank.bankName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </>
        );
      default:
        return (
          <>
            <Grid item xs={12} md={6} lg={4}>
              <TextField
                {...commonProps}
                label='Receiver Name'
                value={receiptForm.receiverName}
                onChange={(e) =>
                  handleReceiptFormChange('receiverName', e.target.value)
                }
              />
            </Grid>

            <Grid item xs={12} md={6} lg={4}>
              <FormControl fullWidth>
                <InputLabel>Bank Name*</InputLabel>
                <Select
                  label='Bank Name*'
                  value={receiptForm.bankId || ''}
                  onChange={(e) => handleBankSelect(e.target.value)}
                  disabled={banksLoading}
                >
                  <MenuItem value=''>
                    {banksLoading ? 'Loading...' : 'Select Bank'}
                  </MenuItem>
                  {banksList.map((bank) => (
                    <MenuItem key={bank.id} value={bank.id}>
                      {bank.bankName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </>
        );
    }
  };

  // Generate installment schedule
  const generateInstallmentSchedule = (
    startDate,
    frequency,
    numberOfInstallments,
    installmentAmount,
  ) => {
    if (
      !startDate ||
      !frequency ||
      !numberOfInstallments ||
      numberOfInstallments <= 0
    ) {
      return [];
    }

    const schedule = [];
    const start = new Date(startDate);
    const startDay = start.getDate();
    const totalDue = getTotalDue();
    let remainingAmount = totalDue;

    for (let i = 0; i < numberOfInstallments; i++) {
      let year = start.getFullYear();
      let month = start.getMonth();

      // Calculate target year/month based on frequency
      if (frequency === 'monthly') {
        month += i;
      } else if (frequency === 'quarterly') {
        month += i * 3;
      } else if (frequency === 'yearly') {
        year += i;
      }

      // Normalize month/year overflow
      year += Math.floor(month / 12);
      month = month % 12;
      if (month < 0) {
        month += 12;
        year -= 1;
      }

      // Find last day of the target month
      const lastDayOfMonth = new Date(year, month + 1, 0).getDate();
      const day = Math.min(startDay, lastDayOfMonth);

      // For the last installment, use remaining amount to avoid rounding errors
      const amount =
        i === numberOfInstallments - 1
          ? remainingAmount
          : Math.min(installmentAmount, remainingAmount);
      remainingAmount -= amount;

      schedule.push({
        srNo: i + 1,
        // Build date string in local calendar without timezone shifts
        dueDate: `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`,
        amount: Math.round(amount),
        paidDate: null,
        status: 'PENDING',
      });
    }

    return schedule;
  };

  // Handle installment plan field changes
  const handleInstallmentPlanChange = (field, value) => {
    const totalDue = getTotalDue();
    let updatedPlan = { ...installmentPlan, [field]: value };

    // Auto-calculate installment amount when number of installments changes
    if (field === 'numberOfInstallments') {
      const numInstallments = parseInt(value) || 0;
      if (numInstallments > 0 && totalDue > 0) {
        updatedPlan.installmentAmount = calculateInstallmentAmount(
          totalDue,
          numInstallments,
        );
      } else {
        updatedPlan.installmentAmount = '';
      }
    }

    setInstallmentPlan(updatedPlan);
  };

  // Get installment schedule
  const installmentSchedule =
    installmentPlan.startDate &&
      installmentPlan.frequency &&
      installmentPlan.numberOfInstallments
      ? generateInstallmentSchedule(
        installmentPlan.startDate,
        installmentPlan.frequency,
        parseInt(installmentPlan.numberOfInstallments) || 0,
        parseFloat(installmentPlan.installmentAmount) || 0,
      )
      : [];

  // Handlers for updating installment status
  const openStatusDialog = (installment) => {
    if (!installment || !existingInstallmentPlan) return;
    setSelectedInstallment(installment);
    setStatusForm({
      // Always default to PAID when opening the dialog, regardless of current status
      status: 'PAID',
      paidDate: installment.paidDate
        ? new Date(installment.paidDate).toISOString().split('T')[0]
        : new Date().toISOString().split('T')[0],
      remark: installment.remark || '',
    });
    setStatusDialogError('');
    setStatusDialogOpen(true);
  };

  const closeStatusDialog = () => {
    setStatusDialogOpen(false);
    setSelectedInstallment(null);
    setStatusDialogError('');
  };

  const handleStatusFormChange = (field, value) => {
    setStatusForm((prev) => ({
      ...prev,
      [field]: value,
    }));
    if (statusDialogError) setStatusDialogError('');
  };

  const handleSaveInstallmentStatus = async () => {
    if (!existingInstallmentPlan || !selectedInstallment) return;

    if (!statusForm.status) {
      setStatusDialogError('Status is required');
      return;
    }

    setStatusDialogSaving(true);
    setStatusDialogError('');

    try {
      // Get current user info for updatedBy
      let currentUserName = 'Admin User';
      try {
        const user = localStorage.getItem('user');
        if (user) {
          const userData = JSON.parse(user);
          currentUserName =
            userData?.user?.name ||
            userData?.name ||
            userData?.username ||
            'Admin User';
        }
      } catch (err) {
        console.error('Error getting user info for installment status:', err);
      }

      const planId = existingInstallmentPlan._id;
      const installmentSrNo = selectedInstallment.srNo;

      const payload = {
        status: statusForm.status,
        paidDate: statusForm.paidDate || null,
        remark: statusForm.remark || '',
        updatedBy: currentUserName,
      };

      const json = await updateInstallmentStatus(
        planId,
        installmentSrNo,
        payload,
      );

      if (!json.status) {
        throw new Error(json.message || 'Failed to update installment status');
      }

      // Update local existingInstallmentPlan with latest data if provided
      if (json.data) {
        setExistingInstallmentPlan(json.data);
      } else {
        // Fallback: update only the selected installment locally
        setExistingInstallmentPlan((prev) => {
          if (!prev) return prev;
          const updatedSchedule = (prev.schedule || []).map((inst) =>
            inst.srNo === installmentSrNo
              ? {
                ...inst,
                status: statusForm.status,
                paidDate: statusForm.paidDate || null,
                remark: statusForm.remark || '',
              }
              : inst,
          );
          return { ...prev, schedule: updatedSchedule };
        });
      }

      closeStatusDialog();
    } catch (err) {
      console.error('Error updating installment status:', err);
      setStatusDialogError(
        err.message || 'Failed to update installment status',
      );
    } finally {
      setStatusDialogSaving(false);
    }
  };

  // Fetch existing installment plan when Installment tab is opened
  useEffect(() => {
    const fetchInstallmentPlan = async () => {
      if (activeTab !== 1) return;

      // Get booking ID similar to receipts / updates
      const bookingIdForInstallment =
        originalData?._id || booking.id || booking._id || id;
      if (!bookingIdForInstallment) {
        setExistingInstallmentPlan(null);
        return;
      }

      setInstallmentPlanLoading(true);
      setInstallmentPlanError('');

      try {
        const json = await getInstallmentPlan(bookingIdForInstallment);

        // Check if response indicates "not found" - this is a valid case, not an error
        if (
          json.status === false &&
          (json.message?.toLowerCase().includes('not found') ||
            json.message
              ?.toLowerCase()
              .includes('installment plan for this booking not found'))
        ) {
          // No plan found - this is expected, show the form to create one
          setExistingInstallmentPlan(null);
          setInstallmentPlanError(''); // Clear any error
        } else if (
          json.status &&
          json.data &&
          Object.keys(json.data).length > 0
        ) {
          // Plan exists
          setExistingInstallmentPlan(json.data);
          setInstallmentPlanError('');
        } else {
          // No plan found for this booking (other cases)
          setExistingInstallmentPlan(null);
          setInstallmentPlanError('');
        }
      } catch (err) {
        console.error('Error fetching installment plan:', err);
        // Only set error for actual errors, not "not found" responses
        const errorMessage = err.message || 'Failed to load installment plan';
        if (errorMessage.toLowerCase().includes('not found')) {
          // Treat "not found" as a valid case, not an error
          setExistingInstallmentPlan(null);
          setInstallmentPlanError('');
        } else {
          setInstallmentPlanError(errorMessage);
          setExistingInstallmentPlan(null);
        }
      } finally {
        setInstallmentPlanLoading(false);
      }
    };

    fetchInstallmentPlan();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  useEffect(() => {
    const fetchInquiryStatuses = async () => {
      try {
        const statuses = await getInquiryStatuses();
        const leadStatuses = (statuses || []).filter(
          (item) => (item.stageType || '').toLowerCase() === 'lead',
        );
        setInquiryStatuses(leadStatuses);
      } catch (err) {
        console.error('Error fetching inquiry statuses:', err);
      }
    };
    fetchInquiryStatuses();
  }, []);

  // Fetch follow-ups when Followup/Remark tab is opened
  useEffect(() => {
    const fetchFollowUps = async () => {
      if (activeTab !== 4) return;

      // Get booking ID
      const bookingIdForFollowUp =
        originalData?._id || booking.id || booking._id || id;
      if (!bookingIdForFollowUp) {
        setFollowUpsError('Booking ID not found');
        setFollowUps([]);
        setFollowUpsTotalCount(0);
        return;
      }

      setFollowUpsLoading(true);
      setFollowUpsError('');

      try {
        const response = await getBookingFollowUps({
          bookingId: bookingIdForFollowUp,
          page: followUpsPage + 1,
          limit: followUpsRowsPerPage,
        });

        // Map response correctly: handle both paginated (docs) and simple array structures
        if (response.status || response.success) {
          const followUpData = response.data;
          if (Array.isArray(followUpData)) {
            setFollowUps(followUpData);
            setFollowUpsTotalCount(followUpData.length);
          } else if (followUpData && Array.isArray(followUpData.docs)) {
            setFollowUps(followUpData.docs);
            setFollowUpsTotalCount(
              followUpData.totalDocs ||
              followUpData.total ||
              followUpData.count ||
              followUpData.docs.length,
            );
          } else {
            setFollowUps([]);
            setFollowUpsTotalCount(0);
          }
        } else {
          setFollowUps([]);
          setFollowUpsTotalCount(0);
        }
      } catch (err) {
        console.error('Error fetching follow-ups:', err);
        setFollowUpsError(err.message || 'Failed to load follow-ups');
        setFollowUps([]);
        setFollowUpsTotalCount(0);
      } finally {
        setFollowUpsLoading(false);
      }
    };

    fetchFollowUps();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, followUpsPage, followUpsRowsPerPage]);

  // Fetch progress when Progress tab is opened
  useEffect(() => {
    const fetchProgress = async () => {
      if (activeTab !== 6) return;

      // Get booking ID
      const bookingIdForProgress =
        originalData?._id || booking.id || booking._id || id;
      if (!bookingIdForProgress) {
        setProgressError('Booking ID not found');
        setProgressData([]);
        return;
      }

      setProgressLoading(true);
      setProgressError('');

      try {
        const response = await getBookingProgress({
          bookingId: bookingIdForProgress,
          page: 1,
          limit: 100, // Get all progress items
        });

        // Handle different response formats
        if (
          response.status &&
          response.data &&
          Array.isArray(response.data.docs)
        ) {
          setProgressData(response.data.docs);
        } else if (
          response.success &&
          response.data &&
          Array.isArray(response.data.docs)
        ) {
          setProgressData(response.data.docs);
        } else if (response.data && Array.isArray(response.data.docs)) {
          setProgressData(response.data.docs);
        } else if (Array.isArray(response.data)) {
          setProgressData(response.data);
        } else if (Array.isArray(response)) {
          setProgressData(response);
        } else {
          setProgressData([]);
        }
      } catch (err) {
        console.error('Error fetching progress:', err);
        setProgressError(err.message || 'Failed to load booking progress');
        setProgressData([]);
      } finally {
        setProgressLoading(false);
      }
    };

    fetchProgress();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const handleFollowUpFormChange = (field, value) => {
    setFollowUpForm((prev) => ({ ...prev, [field]: value }));
    if (followUpSaveError) setFollowUpSaveError('');
    if (followUpSaveSuccess) setFollowUpSaveSuccess('');
  };

  // Handler for creating follow-up
  const handleSaveFollowUp = async () => {
    // Get booking ID
    const bookingIdForFollowUp =
      originalData?._id || booking.id || booking._id || id;

    if (!bookingIdForFollowUp) {
      setFollowUpSaveError('Booking ID not found');
      return;
    }

    // Validate required fields
    if (!followUpForm.remark || followUpForm.remark.trim() === '') {
      setFollowUpSaveError('Remark is required');
      return;
    }
    if (!followUpForm.status) {
      setFollowUpSaveError('Status is required');
      return;
    }

    setSavingFollowUp(true);
    setFollowUpSaveError('');
    setFollowUpSaveSuccess('');

    try {
      const payload = {
        bookingId: bookingIdForFollowUp,
        followUpDate: followUpForm.date,
        nextFollowUpDate: followUpForm.nextFollowUpDate,
        status: followUpForm.status,
        remark: followUpForm.remark.trim(),
        description: followUpForm.remark.trim(),
      };

      const response = await createBookingFollowUp(payload);

      if (response.success || response.status) {
        setFollowUpSaveSuccess('Follow-up saved successfully!');
        // Reset form
        setFollowUpForm({
          date: new Date().toISOString(),
          nextFollowUpDate: '',
          status: '',
          remark: '',
        });
        setFollowUpSaveError('');

        // Refresh follow-ups list
        const followUpResponse = await getBookingFollowUps({
          bookingId: bookingIdForFollowUp,
          page: 1,
          limit: 100,
        });

        if (followUpResponse.status || followUpResponse.success) {
          const followUpData = followUpResponse.data;
          if (Array.isArray(followUpData)) {
            setFollowUps(followUpData);
          } else if (followUpData && Array.isArray(followUpData.docs)) {
            setFollowUps(followUpData.docs);
          }
        }
      } else {
        setFollowUpSaveError(response.message || 'Failed to save follow-up');
      }
    } catch (err) {
      console.error('Error saving follow-up:', err);
      setFollowUpSaveError(
        err.message || 'Error occurred while saving follow-up',
      );
    } finally {
      setSavingFollowUp(false);
    }
  };


  // Toggle expandable row in grouped view
  const toggleRowExpansion = (rowId) => {
    setExpandedGroupedRows((prev) => ({
      ...prev,
      [rowId]: !prev[rowId],
    }));
  };

  // Fetch documents when Documents tab is opened
  useEffect(() => {
    const fetchDocuments = async () => {
      if (activeTab !== 3) return;

      // Get booking ID
      const bookingIdForDocuments =
        originalData?._id || booking.id || booking._id || id;
      if (!bookingIdForDocuments) {
        setDocumentsError('Booking ID not found');
        setDocuments([]);
        return;
      }

      setDocumentsLoading(true);
      setDocumentsError('');

      try {
        const response = await getBookingDocuments(bookingIdForDocuments);

        // Extract documents from response
        if (
          response.success &&
          response.data &&
          Array.isArray(response.data.documents)
        ) {
          setDocuments(response.data.documents);
        } else {
          setDocuments([]);
        }
      } catch (err) {
        console.error('Error fetching documents:', err);
        setDocumentsError(err.message || 'Failed to load documents');
        setDocuments([]);
      } finally {
        setDocumentsLoading(false);
      }
    };

    fetchDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Handle document upload
  const handleUploadDocument = async () => {
    if (!documentUpload.type || !documentUpload.file || !documentUpload.name) {
      setDocumentUploadError('Please fill all required fields');
      return;
    }

    const bookingId = originalData?._id || booking.id || booking._id || id;
    if (!bookingId) {
      setDocumentUploadError('Booking ID not found');
      return;
    }

    setUploadingDocument(true);
    setDocumentUploadError('');
    setDocumentUploadSuccess(false);

    try {
      // Get current user info for uploadedBy
      let currentUserName = 'Admin User';
      try {
        const user = localStorage.getItem('user');
        if (user) {
          const userData = JSON.parse(user);
          currentUserName =
            userData?.user?.name ||
            userData?.name ||
            userData?.username ||
            'Admin User';
        }
      } catch (err) {
        console.error('Error getting user info:', err);
      }

      // Generate unique filename
      const fileExtension = documentUpload.file.name.split('.').pop();
      const timestamp = Date.now();
      const fileName = `booking/${bookingId}-${documentUpload.type}-${timestamp}.${fileExtension}`;

      // Get signed URL for upload
      const filesForSignedUrl = [
        {
          category: 'booking',
          fileName: fileName,
          fileType: documentUpload.file.type || 'application/octet-stream',
        },
      ];

      const signedUrls = await getSignedUrls(filesForSignedUrl);
      if (!signedUrls || signedUrls.length === 0) {
        throw new Error('Failed to get signed URL for upload');
      }

      const signedUrlInfo = signedUrls[0];
      if (!signedUrlInfo || !signedUrlInfo.uploadUrl) {
        throw new Error('Invalid signed URL response');
      }

      // Upload file to S3
      const uploadSuccess = await uploadFileToS3(
        signedUrlInfo.uploadUrl,
        documentUpload.file,
        documentUpload.file.type || 'application/octet-stream',
      );

      if (!uploadSuccess) {
        throw new Error('Failed to upload file to S3');
      }

      // Extract full S3 URL (remove query parameters)
      const s3Url = signedUrlInfo.uploadUrl.split('?')[0];

      // Verify we have a full URL
      if (
        !s3Url ||
        (!s3Url.startsWith('http://') && !s3Url.startsWith('https://'))
      ) {
        throw new Error('Invalid S3 URL generated');
      }

      // Call API to add document to booking
      const payload = {
        document: {
          type: documentUpload.type,
          url: s3Url,
          name: documentUpload.name,
          uploadedBy: currentUserName,
        },
      };

      const response = await updateBooking(bookingId, payload);

      if (response.success || response.status) {
        setDocumentUploadSuccess(true);
        // Reset form
        setDocumentUpload({
          type: '',
          file: null,
          name: '',
        });

        // Refresh documents list by fetching from API
        const bookingIdForRefresh =
          originalData?._id || booking.id || booking._id || id;
        if (bookingIdForRefresh) {
          try {
            const fetchResponse =
              await getBookingDocuments(bookingIdForRefresh);
            if (
              fetchResponse.success &&
              fetchResponse.data &&
              Array.isArray(fetchResponse.data.documents)
            ) {
              setDocuments(fetchResponse.data.documents);
            }
          } catch (fetchErr) {
            console.error('Error refreshing documents:', fetchErr);
            // Fallback: add the new document to the list
            const newDocument = {
              type: documentUpload.type,
              url: s3Url,
              name: documentUpload.name,
              uploadedBy: currentUserName,
              uploadedAt: new Date().toISOString(),
            };
            setDocuments((prev) => [...prev, newDocument]);
          }
        }

        // Clear success message after 3 seconds
        setTimeout(() => setDocumentUploadSuccess(false), 3000);
      } else {
        throw new Error(
          response.message || 'Failed to add document to booking',
        );
      }
    } catch (error) {
      console.error('Error uploading document:', error);
      setDocumentUploadError(error.message || 'Failed to upload document');
    } finally {
      setUploadingDocument(false);
    }
  };

  // Handle document download
  const handleDownloadDocument = async (doc) => {
    try {
      // Prefer viewUrl from API response if available
      if (doc.viewUrl) {
        window.open(doc.viewUrl, '_blank');
        return;
      }

      // Fallback: use viewUrl function to get signed URL
      if (doc.url) {
        const viewUrlResult = await viewUrl(doc.url);
        if (viewUrlResult) {
          window.open(viewUrlResult, '_blank');
        } else {
          // Last fallback: try direct URL
          window.open(doc.url, '_blank');
        }
      } else {
        alert('Document URL not available');
      }
    } catch (error) {
      console.error('Error downloading document:', error);
      alert('Failed to download document');
    }
  };

  // Handle document delete
  const handleDeleteDocument = async (doc) => {
    if (
      !window.confirm(
        `Are you sure you want to delete "${doc.name || doc.type}"?`,
      )
    ) {
      return;
    }

    const bookingId = originalData?._id || booking.id || booking._id || id;
    if (!bookingId) {
      alert('Booking ID not found');
      return;
    }

    try {
      // Remove document from list (filter out the deleted one)
      // Note: You may need to implement a DELETE API endpoint for documents
      // For now, we'll just remove it from the local state
      setDocuments((prev) =>
        prev.filter((d) => d._id !== doc._id && d.url !== doc.url),
      );

      // TODO: Call API to delete document if endpoint exists
      // await deleteBookingDocument(bookingId, doc._id);

      // Refresh documents list from API to ensure consistency
      try {
        const fetchResponse = await getBookingDocuments(bookingId);
        if (
          fetchResponse.success &&
          fetchResponse.data &&
          Array.isArray(fetchResponse.data.documents)
        ) {
          setDocuments(fetchResponse.data.documents);
        }
      } catch (fetchErr) {
        console.error('Error refreshing documents after delete:', fetchErr);
      }

      alert('Document deleted successfully');
    } catch (error) {
      console.error('Error deleting document:', error);
      alert('Failed to delete document');
    }
  };

  // Determine which installment (if any) can be updated next:
  // Only the earliest installment with status PENDING / PARTIALLY_PAID / OVERDUE
  const nextUpdatableSrNo =
    existingInstallmentPlan && Array.isArray(existingInstallmentPlan.schedule)
      ? ((
        existingInstallmentPlan.schedule.find((inst) => {
          const status = (inst.status || 'PENDING').toUpperCase();
          return (
            status === 'PENDING' ||
            status === 'PARTIALLY_PAID' ||
            status === 'OVERDUE'
          );
        }) || {}
      ).srNo ?? null)
      : null;

  const isFullyPaid =
    (financialFields.totalDueAmount ??
      Math.max(
        0,
        (financialFields.propertyAmount ?? 0) -
        (financialFields.totalReceived ?? 0),
      )) <= 0 && (financialFields.propertyAmount ?? 0) > 0;

  return (
    <Box sx={{ p: 1, mr: 6 }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={handleBack} sx={{ bgcolor: '#f5f5f5' }}>
            <ArrowBack />
          </IconButton>
          <Box>
            <Typography variant='h4' fontWeight='bold' color='primary'>
              Booking Details - {booking.bookingId}
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              {booking.customer} • Plot {booking.plotNo}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {!isEditMode ? (
            <>
              {canWriteBooking && (
                <Button
                  variant='contained'
                  startIcon={<Edit />}
                  onClick={() => setIsEditMode(true)}
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                  }}
                >
                  Edit Details
                </Button>
              )}
            </>
          ) : (
            <>
              <Button
                variant='outlined'
                onClick={() => {
                  // Reset financial fields to original values
                  setFinancialFields({
                    propertyAmount: booking.propertyAmount ?? 0,
                    bookingAmount: booking.bookingAmount ?? 0,
                    discount: booking.discount ?? 0,
                    totalReceived: booking.totalReceived ?? 0,
                    totalDueAmount: booking.totalDueAmount ?? 0,
                    plotAmount:
                      booking.plotAmount ?? booking.propertyAmount ?? 0,
                  });
                  setIsEditMode(false);
                }}
                sx={{ borderRadius: 2 }}
              >
                Cancel
              </Button>
              <Button
                variant='contained'
                startIcon={
                  saving ? (
                    <CircularProgress size={18} color='inherit' />
                  ) : (
                    <Save />
                  )
                }
                onClick={handleSave}
                disabled={saving}
                sx={{ borderRadius: 2 }}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </>
          )}
        </Box>
      </Box>

      {/* Status Banner */}
      <Paper
        sx={{
          p: 2,
          mb: 3,
          bgcolor: isFullyPaid
            ? 'success.light'
            : booking.status === 'booked' || booking.status === 'pending'
              ? 'warning.light'
              : booking.status === 'alloted'
                ? 'success.light'
                : booking.status === 'cancelled'
                  ? 'error.light'
                  : 'warning.light',
          borderLeft: 4,
          borderColor: isFullyPaid
            ? 'success.main'
            : booking.status === 'booked' || booking.status === 'pending'
              ? 'warning.main'
              : booking.status === 'alloted'
                ? 'success.main'
                : booking.status === 'cancelled'
                  ? 'error.main'
                  : 'warning.main',
          borderRadius: 2,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Box>
            <Typography variant='h6' fontWeight='bold' color='text.primary'>
              Status:{' '}
              {isFullyPaid
                ? 'FULLY PAID'
                : (booking.status || 'pending').toUpperCase()}
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              Booking Date:{' '}
              {booking.plotBookingDate || booking.bookingDate || ''}
            </Typography>
          </Box>
          <Chip
            label={
              isFullyPaid
                ? 'FULLY PAID'
                : booking.status === 'booked' || booking.status === 'pending'
                  ? 'PAYMENT PENDING'
                  : booking.status === 'alloted'
                    ? 'COMPLETED'
                    : booking.status === 'cancelled'
                      ? 'CANCELLED'
                      : 'PENDING'
            }
            color={
              isFullyPaid
                ? 'success'
                : booking.status === 'booked' || booking.status === 'pending'
                  ? 'warning'
                  : booking.status === 'alloted'
                    ? 'success'
                    : booking.status === 'cancelled'
                      ? 'error'
                      : 'default'
            }
            sx={{ fontWeight: 700 }}
          />
        </Box>
      </Paper>

      {/* Tabs */}
      <Paper sx={{ borderRadius: 2, mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          variant='scrollable'
          scrollButtons='auto'
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 600,
              minHeight: 56,
            },
          }}
        >
          <Tab icon={<Receipt />} iconPosition='start' label='Booking' />
          <Tab
            icon={<AccountBalance />}
            iconPosition='start'
            label='Installment'
          />
          {canViewReceipt && (
            <Tab icon={<Receipt />} iconPosition='start' label='Receipt' />
          )}
          {canViewDocument && (
            <Tab icon={<Description />} iconPosition='start' label='Document' />
          )}
          {canViewFollowup && (
            <Tab
              icon={<Comment />}
              iconPosition='start'
              label='Followup/Remark'
            />
          )}
          {canViewCommission && (
            <Tab icon={<MoneyOff />} iconPosition='start' label='Commission' />
          )}
          {canViewProgress && (
            <Tab icon={<Timeline />} iconPosition='start' label='Progress' />
          )}
          {canViewStatus && (
            <Tab
              icon={<CheckCircle />}
              iconPosition='start'
              label='Booking Status'
            />
          )}
        </Tabs>
      </Paper>

      {/* Tab Content */}
      <Box>
        {/* Tab 0: Booking */}
        {activeTabLabel === 'Booking' && (
          <Box>
            <Grid container spacing={3}>
              {/* Basic Info */}
              <Grid item xs={12}>
                <Paper sx={{ p: 3, borderRadius: 2 }}>
                  <Typography
                    variant='h6'
                    fontWeight='bold'
                    color='primary'
                    gutterBottom
                  >
                    Booking Information
                  </Typography>
                  <Divider sx={{ mb: 3 }} />
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label='Booking ID'
                        value={booking.bookingId}
                        disabled
                        variant='outlined'
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label='Booking Date'
                        type='date'
                        value={booking.plotBookingDate}
                        disabled
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label='Customer Name'
                        value={booking.customer}
                        disabled
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label='Associate/Agent'
                        value={booking.associate}
                        disabled
                      />
                    </Grid>
                    {/* <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Customer ID"
                        value={booking.customerId || ''}
                        disabled
                        variant="outlined"
                      />
                    </Grid> */}
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label='Customer No'
                        value={booking.customerNo || ''}
                        disabled
                        variant='outlined'
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label='Customer Phone'
                        value={booking.customerDetails?.phone || ''}
                        disabled
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label='Customer Alt Phone'
                        value={booking.customerDetails?.altPhone || ''}
                        disabled
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label='Customer Email'
                        value={booking.customerDetails?.email || ''}
                        disabled
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label='Customer PAN'
                        value={booking.customerDetails?.pan || ''}
                        disabled
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label='Customer Address'
                        multiline
                        rows={2}
                        value={booking.customerDetails?.address || ''}
                        disabled
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label='Customer City'
                        value={booking.customerDetails?.city || ''}
                        disabled
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label='Customer State'
                        value={booking.customerDetails?.state || ''}
                        disabled
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label='Customer Gender'
                        value={booking.customerDetails?.gender || ''}
                        disabled
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label='Customer DOB'
                        value={
                          booking.customerDetails?.dob
                            ? moment(booking.customerDetails.dob).format(
                              'DD-MM-YYYY',
                            )
                            : ''
                        }
                        disabled
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label='Customer Aadhar'
                        value={booking.customerDetails?.aadhar || ''}
                        disabled
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label='Property/Plot Number'
                        value={
                          booking.plotNo ||
                          booking.particular?.inventoryDetail ||
                          ''
                        }
                        disabled
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label='Inventory Type'
                        value={
                          booking.inventoryType ||
                          booking.particular?.inventoryType ||
                          ''
                        }
                        disabled
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label='Project Name'
                        value={
                          booking.branchName ||
                          booking.particular?.projectName ||
                          ''
                        }
                        disabled
                      />
                    </Grid>
                    {/* <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Project ID"
                        value={booking.particular?.projectId || ''}
                        disabled
                        variant="outlined"
                      />
                    </Grid> */}
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label='Area Size'
                        value={
                          booking.inventoryDetails?.totalSize ||
                          booking.particular?.areaSize ||
                          ''
                        }
                        disabled
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label='Dimensions (W x L)'
                        value={
                          booking.inventoryDetails?.width ||
                            booking.inventoryDetails?.length
                            ? `${booking.inventoryDetails?.width || '0'} x ${booking.inventoryDetails?.length || '0'} ft`
                            : booking.particular?.width ||
                              booking.particular?.plotWidth
                              ? `${booking.particular?.width || booking.particular?.plotWidth || '0'} x ${booking.particular?.length || booking.particular?.plotHeight || '0'} ft`
                              : 'N/A'
                        }
                        disabled
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label='Rate (₹/sq ft)'
                        value={
                          booking.inventoryDetails?.pricePerSqFt ||
                            booking.particular?.plotRate ||
                            booking.particular?.pricePerSqFt
                            ? `₹${Number(booking.inventoryDetails?.pricePerSqFt || booking.particular?.plotRate || booking.particular?.pricePerSqFt).toLocaleString('en-IN')}`
                            : 'N/A'
                        }
                        disabled
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label='Facing'
                        value={booking.particular?.facing || ''}
                        disabled
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label='Associate Name'
                        defaultValue={
                          booking.associate ||
                          booking.associateDetails?.name ||
                          ''
                        }
                        disabled
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label='Associate Phone'
                        defaultValue={booking.associateDetails?.phone || ''}
                        disabled
                      />
                    </Grid>
                    {/* <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Associate ID"
                        value={booking.associateDetails?.associateId || ''}
                        disabled
                        variant="outlined"
                      />
                    </Grid> */}
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth>
                        <InputLabel>Payment Plan</InputLabel>
                        <Select
                          value={booking.paymentPlan || ''}
                          label='Payment Plan'
                          disabled
                        >
                          <MenuItem value='EMI'>EMI</MenuItem>
                          <MenuItem value='Lump Sum'>Lump Sum</MenuItem>
                          <MenuItem value='Flexi'>Flexi</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth>
                        <InputLabel>Payment Mode</InputLabel>
                        <Select
                          value={booking.mode || ''}
                          label='Payment Mode'
                          disabled
                        >
                          <MenuItem value='Cash'>Cash</MenuItem>
                          <MenuItem value='Cheque'>Cheque</MenuItem>
                          <MenuItem value='RTGS/NEFT'>RTGS/NEFT</MenuItem>
                          <MenuItem value='UPI'>UPI</MenuItem>
                          <MenuItem value='Other'>Other</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth>
                        <InputLabel>Status</InputLabel>
                        <Select
                          value={booking.status || 'pending'}
                          label='Status'
                          disabled
                        >
                          <MenuItem value='pending'>Pending</MenuItem>
                          <MenuItem value='booked'>Booked</MenuItem>
                          <MenuItem value='alloted'>Alloted</MenuItem>
                          <MenuItem value='cancelled'>Cancelled</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label='Remark'
                        multiline
                        rows={2}
                        value={booking.remark || ''}
                        disabled
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label='Created By'
                        value={booking.createdBy || ''}
                        disabled
                        variant='outlined'
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label='Updated By'
                        value={booking.updatedBy || ''}
                        disabled
                        variant='outlined'
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label='Property Amount'
                        type='number'
                        value={financialFields.propertyAmount}
                        onChange={(e) =>
                          handleFinancialFieldChange(
                            'propertyAmount',
                            e.target.value,
                          )
                        }
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position='start'>₹</InputAdornment>
                          ),
                        }}
                        disabled
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label='Booking Amount'
                        type='number'
                        value={financialFields.bookingAmount}
                        onChange={(e) =>
                          handleFinancialFieldChange(
                            'bookingAmount',
                            e.target.value,
                          )
                        }
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position='start'>₹</InputAdornment>
                          ),
                        }}
                        disabled
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label='Discount Amount'
                        type='number'
                        value={financialFields.discount}
                        onChange={(e) =>
                          handleFinancialFieldChange('discount', e.target.value)
                        }
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position='start'>₹</InputAdornment>
                          ),
                        }}
                        disabled
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label='Total Received'
                        type='number'
                        value={financialFields.totalReceived}
                        onChange={(e) =>
                          handleFinancialFieldChange(
                            'totalReceived',
                            e.target.value,
                          )
                        }
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position='start'>₹</InputAdornment>
                          ),
                        }}
                        disabled
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label='Total Due Amount'
                        type='number'
                        value={financialFields.totalDueAmount}
                        onChange={(e) =>
                          handleFinancialFieldChange(
                            'totalDueAmount',
                            e.target.value,
                          )
                        }
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position='start'>₹</InputAdornment>
                          ),
                        }}
                        disabled
                      />
                    </Grid>
                    {/* <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Plot Amount (Net)"
                        type="number"
                        value={financialFields.plotAmount}
                        onChange={(e) => handleFinancialFieldChange('plotAmount', e.target.value)}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                        }}
                      />
                    </Grid> */}
                  </Grid>
                </Paper>
              </Grid>

              {/* Additional Applicants */}
              {(() => {
                const additional =
                  booking._original?.additionalCustomers ||
                  booking.additionalCustomers ||
                  [];
                if (Array.isArray(additional) && additional.length > 0) {
                  return (
                    <Grid item xs={12}>
                      <Box sx={{ mt: 3 }}>
                        <Typography
                          variant='h6'
                          fontWeight='bold'
                          color='primary'
                          gutterBottom
                        >
                          Additional Applicants
                        </Typography>
                        <Paper sx={{ borderRadius: 2, mb: 2 }}>
                          <TableContainer>
                            <Table size='small'>
                              <TableHead>
                                <TableRow>
                                  <TableCell>
                                    <Typography variant='subtitle2'>
                                      Name
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant='subtitle2'>
                                      Mobile
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant='subtitle2'>
                                      Alternate
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant='subtitle2'>
                                      Relation
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant='subtitle2'>
                                      Email
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {additional.map((c, i) => (
                                  <TableRow key={`additional-${i}`}>
                                    <TableCell>{c.name || '-'}</TableCell>
                                    <TableCell>{c.mobile || '-'}</TableCell>
                                    <TableCell>
                                      {c.alternateMobile || '-'}
                                    </TableCell>
                                    <TableCell>{c.relation || '-'}</TableCell>
                                    <TableCell>{c.email || '-'}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Paper>
                      </Box>
                    </Grid>
                  );
                }
                return null;
              })()}

              {/* Financial Summary */}
              <Grid item xs={12}>
                <Paper
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    bgcolor: '#f8f9fa',
                    borderLeft: 4,
                    borderColor: 'secondary.main',
                  }}
                >
                  <Typography
                    variant='h6'
                    fontWeight='bold'
                    color='primary'
                    gutterBottom
                  >
                    Financial Summary
                  </Typography>
                  <Grid container spacing={3} sx={{ mt: 1 }}>
                    <Grid item xs={12} sm={6} md={2}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography
                          variant='h4'
                          fontWeight='bold'
                          color='text.primary'
                        >
                          {formatCurrency(financialFields.propertyAmount ?? 0)}
                        </Typography>
                        <Typography variant='body2' color='text.secondary'>
                          Property Amount
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography
                          variant='h4'
                          fontWeight='bold'
                          color='primary.main'
                        >
                          {formatCurrency(financialFields.bookingAmount ?? 0)}
                        </Typography>
                        <Typography variant='body2' color='text.secondary'>
                          Booking Amount
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography
                          variant='h4'
                          fontWeight='bold'
                          color='success.main'
                        >
                          {formatCurrency(financialFields.totalReceived ?? 0)}
                        </Typography>
                        <Typography variant='body2' color='text.secondary'>
                          Total Received
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography
                          variant='h4'
                          fontWeight='bold'
                          color='info.main'
                        >
                          {formatCurrency(financialFields.discount ?? 0)}
                        </Typography>
                        <Typography variant='body2' color='text.secondary'>
                          Discount
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography
                          variant='h4'
                          fontWeight='bold'
                          color='warning.main'
                        >
                          {formatCurrency(
                            financialFields.totalAuthorityCharge ?? 0,
                          )}
                        </Typography>
                        <Typography variant='body2' color='text.secondary'>
                          Authority Charges
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography
                          variant='h4'
                          fontWeight='bold'
                          color='error.main'
                        >
                          {formatCurrency(
                            financialFields.totalDueAmount ??
                            Math.max(
                              0,
                              (financialFields.propertyAmount ?? 0) -
                              (financialFields.totalReceived ?? 0),
                            ),
                          )}
                        </Typography>
                        <Typography variant='body2' color='text.secondary'>
                          Total Due
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              {/* Payment Progress */}
              <Grid item xs={12}>
                <Paper sx={{ p: 3, borderRadius: 2, bgcolor: '#f8f9fa' }}>
                  <Typography
                    variant='h6'
                    fontWeight='bold'
                    color='primary'
                    gutterBottom
                  >
                    Payment Progress
                  </Typography>
                  <Box sx={{ mt: 2 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        mb: 1,
                      }}
                    >
                      <Typography variant='body2' fontWeight='600'>
                        {getPaymentProgress(
                          financialFields.totalReceived ?? 0,
                          financialFields.propertyAmount ?? 0,
                        ).toFixed(1)}
                        % Completed
                      </Typography>
                      <Typography variant='body2' fontWeight='600'>
                        ₹{(financialFields.totalReceived ?? 0).toLocaleString('en-IN')}{' '}
                        / ₹
                        {(financialFields.propertyAmount ?? 0).toLocaleString('en-IN')}
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant='determinate'
                      value={getPaymentProgress(
                        financialFields.totalReceived ?? 0,
                        financialFields.propertyAmount ?? 0,
                      )}
                      color='primary'
                      sx={{ height: 12, borderRadius: 6 }}
                    />
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        mt: 2,
                      }}
                    >
                      <Box>
                        <Typography variant='caption' color='text.secondary'>
                          Booking Amount
                        </Typography>
                        <Typography variant='body2' fontWeight='600'>
                          ₹
                          {(
                            financialFields.bookingAmount ?? 0
                          ).toLocaleString('en-IN')}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant='caption' color='text.secondary'>
                          Discount
                        </Typography>
                        <Typography
                          variant='body2'
                          fontWeight='600'
                          color='info.main'
                        >
                          ₹{(financialFields.discount ?? 0).toLocaleString('en-IN')}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant='caption' color='text.secondary'>
                          Authority Charges
                        </Typography>
                        <Typography
                          variant='body2'
                          fontWeight='600'
                          color='warning.main'
                        >
                          ₹
                          {(
                            financialFields.totalAuthorityCharge ?? 0
                          ).toLocaleString('en-IN')}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant='caption' color='text.secondary'>
                          Total Due
                        </Typography>
                        <Typography
                          variant='body2'
                          fontWeight='600'
                          color='error.main'
                        >
                          ₹
                          {(
                            financialFields.totalDueAmount ??
                            Math.max(
                              0,
                              (financialFields.propertyAmount ?? 0) -
                              (financialFields.totalReceived ?? 0),
                            )
                          ).toLocaleString('en-IN')}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Tab 1: Installment */}
        {activeTabLabel === 'Installment' && (
          <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Typography
              variant='h6'
              fontWeight='bold'
              color='primary'
              gutterBottom
            >
              Installment Plan
            </Typography>
            <Divider sx={{ mb: 3 }} />

            {installmentPlanLoading ? (
              <Box sx={{ py: 4, textAlign: 'center' }}>
                <CircularProgress size={24} />
                <Typography
                  variant='body2'
                  color='text.secondary'
                  sx={{ mt: 1 }}
                >
                  Loading installment plan...
                </Typography>
              </Box>
            ) : installmentPlanError ? (
              <Alert severity='error' sx={{ mb: 2 }}>
                {installmentPlanError}
              </Alert>
            ) : (
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  {/* If plan already exists, show it in read-only mode */}
                  {existingInstallmentPlan ? (
                    <>
                      <Alert severity='info' sx={{ mb: 2 }}>
                        Installment plan is already created for this booking.
                      </Alert>
                      <Paper sx={{ p: 2, bgcolor: '#f8f9fa', mb: 2 }}>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={3}>
                            <TextField
                              fullWidth
                              label='Total Due'
                              type='number'
                              value={
                                existingInstallmentPlan.totalDue ??
                                getTotalDue()
                              }
                              disabled
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position='start'>
                                    ₹
                                  </InputAdornment>
                                ),
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} md={3}>
                            <TextField
                              fullWidth
                              label='Number of Installments'
                              type='number'
                              value={
                                existingInstallmentPlan.numberOfInstallments ??
                                ''
                              }
                              disabled
                            />
                          </Grid>
                          <Grid item xs={12} md={3}>
                            <TextField
                              fullWidth
                              label='Installment Amount'
                              type='number'
                              value={
                                existingInstallmentPlan.installmentAmount ?? ''
                              }
                              disabled
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position='start'>
                                    ₹
                                  </InputAdornment>
                                ),
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} md={3}>
                            <TextField
                              fullWidth
                              label='Start Date'
                              type='date'
                              value={
                                existingInstallmentPlan.startDate
                                  ? new Date(existingInstallmentPlan.startDate)
                                    .toISOString()
                                    .split('T')[0]
                                  : ''
                              }
                              disabled
                              InputLabelProps={{ shrink: true }}
                            />
                          </Grid>
                          <Grid item xs={12} md={3}>
                            <TextField
                              fullWidth
                              label='Frequency'
                              value={existingInstallmentPlan.frequency || ''}
                              disabled
                            />
                          </Grid>
                          <Grid item xs={12} md={3}>
                            <TextField
                              fullWidth
                              label='Plan Status'
                              value={
                                existingInstallmentPlan.planStatus || 'ACTIVE'
                              }
                              disabled
                            />
                          </Grid>
                        </Grid>
                      </Paper>

                      <TableContainer component={Paper}>
                        <Table size='small'>
                          <TableHead>
                            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                              <TableCell>
                                <strong>Sr No.</strong>
                              </TableCell>
                              <TableCell>
                                <strong>Due Date</strong>
                              </TableCell>
                              <TableCell>
                                <strong>Amount</strong>
                              </TableCell>
                              <TableCell>
                                <strong>Paid Date</strong>
                              </TableCell>
                              <TableCell>
                                <strong>Status</strong>
                              </TableCell>
                              <TableCell>
                                <strong>Remark</strong>
                              </TableCell>
                              {isEditMode && (
                                <TableCell>
                                  <strong>Actions</strong>
                                </TableCell>
                              )}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {(existingInstallmentPlan.schedule || []).map(
                              (inst) => {
                                const dueDate = inst.dueDate
                                  ? new Date(inst.dueDate)
                                    .toISOString()
                                    .split('T')[0]
                                  : '-';
                                const paidDate = inst.paidDate
                                  ? new Date(inst.paidDate)
                                    .toISOString()
                                    .split('T')[0]
                                  : null;
                                const status = inst.status || 'PENDING';
                                const remark = inst.remark || '-';

                                return (
                                  <TableRow key={inst.srNo}>
                                    <TableCell>{inst.srNo}</TableCell>
                                    <TableCell>{dueDate}</TableCell>
                                    <TableCell>
                                      ₹{(inst.amount ?? 0).toLocaleString('en-IN')}
                                    </TableCell>
                                    <TableCell>{paidDate || '-'}</TableCell>
                                    <TableCell>
                                      <Chip
                                        label={status}
                                        size='small'
                                        color={
                                          status === 'PAID'
                                            ? 'success'
                                            : status === 'OVERDUE'
                                              ? 'error'
                                              : 'warning'
                                        }
                                      />
                                    </TableCell>
                                    <TableCell>{remark}</TableCell>
                                    {isEditMode && (
                                      <TableCell>
                                        {nextUpdatableSrNo === inst.srNo &&
                                          canWriteBooking && (
                                            <Button
                                              variant='outlined'
                                              size='small'
                                              color='primary'
                                              sx={{
                                                textTransform: 'none',
                                                fontWeight: 600,
                                              }}
                                              onClick={() =>
                                                openStatusDialog(inst)
                                              }
                                            >
                                              Update
                                            </Button>
                                          )}
                                      </TableCell>
                                    )}
                                  </TableRow>
                                );
                              },
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </>
                  ) : (
                    <>
                      {/* No existing plan: show form only in edit mode */}
                      {isEditMode ? (
                        <>
                          <Paper sx={{ p: 2, bgcolor: '#f8f9fa', mb: 2 }}>
                            <Grid container spacing={2}>
                              <Grid item xs={12} md={3}>
                                <TextField
                                  fullWidth
                                  label='Total Due'
                                  type='number'
                                  value={getTotalDue()}
                                  disabled
                                  InputProps={{
                                    startAdornment: (
                                      <InputAdornment position='start'>
                                        ₹
                                      </InputAdornment>
                                    ),
                                  }}
                                />
                              </Grid>
                              <Grid item xs={12} md={3}>
                                <TextField
                                  fullWidth
                                  label='Number of Installments'
                                  type='number'
                                  value={installmentPlan.numberOfInstallments}
                                  onChange={(e) =>
                                    handleInstallmentPlanChange(
                                      'numberOfInstallments',
                                      e.target.value,
                                    )
                                  }
                                  inputProps={{ min: 1 }}
                                />
                              </Grid>
                              <Grid item xs={12} md={3}>
                                <TextField
                                  fullWidth
                                  label='Installment Amount'
                                  type='number'
                                  value={installmentPlan.installmentAmount}
                                  disabled
                                  InputProps={{
                                    startAdornment: (
                                      <InputAdornment position='start'>
                                        ₹
                                      </InputAdornment>
                                    ),
                                  }}
                                />
                              </Grid>
                              <Grid item xs={12} md={3}>
                                <TextField
                                  fullWidth
                                  label='Start Date'
                                  type='date'
                                  value={installmentPlan.startDate}
                                  onChange={(e) =>
                                    handleInstallmentPlanChange(
                                      'startDate',
                                      e.target.value,
                                    )
                                  }
                                  InputLabelProps={{ shrink: true }}
                                />
                              </Grid>
                              <Grid item xs={12} md={3}>
                                <FormControl fullWidth>
                                  <InputLabel>Frequency</InputLabel>
                                  <Select
                                    label='Frequency'
                                    value={installmentPlan.frequency}
                                    onChange={(e) =>
                                      handleInstallmentPlanChange(
                                        'frequency',
                                        e.target.value,
                                      )
                                    }
                                  >
                                    <MenuItem value='monthly'>Monthly</MenuItem>
                                    <MenuItem value='quarterly'>
                                      Quarterly
                                    </MenuItem>
                                    <MenuItem value='yearly'>Yearly</MenuItem>
                                  </Select>
                                </FormControl>
                              </Grid>
                            </Grid>
                          </Paper>
                          {installmentSchedule.length > 0 ? (
                            <TableContainer component={Paper}>
                              <Table size='small'>
                                <TableHead>
                                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                                    <TableCell>
                                      <strong>Sr No.</strong>
                                    </TableCell>
                                    <TableCell>
                                      <strong>Due Date</strong>
                                    </TableCell>
                                    <TableCell>
                                      <strong>Amount</strong>
                                    </TableCell>
                                    <TableCell>
                                      <strong>Paid Date</strong>
                                    </TableCell>
                                    <TableCell>
                                      <strong>Status</strong>
                                    </TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {installmentSchedule.map((installment) => (
                                    <TableRow key={installment.srNo}>
                                      <TableCell>{installment.srNo}</TableCell>
                                      <TableCell>
                                        {installment.dueDate}
                                      </TableCell>
                                      <TableCell>
                                        ₹{installment.amount.toLocaleString('en-IN')}
                                      </TableCell>
                                      <TableCell>
                                        {installment.paidDate || '-'}
                                      </TableCell>
                                      <TableCell>
                                        <Chip
                                          label={installment.status}
                                          size='small'
                                          color={
                                            installment.status === 'PAID'
                                              ? 'success'
                                              : 'warning'
                                          }
                                        />
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          ) : (
                            <Paper
                              sx={{
                                p: 3,
                                textAlign: 'center',
                                bgcolor: '#f8f9fa',
                              }}
                            >
                              <Typography
                                variant='body2'
                                color='text.secondary'
                              >
                                Please select Number of Installments, Start
                                Date, and Frequency to generate the installment
                                schedule.
                              </Typography>
                            </Paper>
                          )}
                        </>
                      ) : (
                        <Paper
                          sx={{ p: 3, textAlign: 'center', bgcolor: '#f8f9fa' }}
                        >
                          <Typography
                            variant='body1'
                            color='text.secondary'
                            gutterBottom
                          >
                            No installment plan found for this booking.
                          </Typography>
                          <Typography variant='body2' color='text.secondary'>
                            Please enable Edit mode to create a new installment
                            plan.
                          </Typography>
                        </Paper>
                      )}
                    </>
                  )}
                </Grid>
              </Grid>
            )}
          </Paper>
        )}

        {/* Tab 2: Receipt */}
        {activeTabLabel === 'Receipt' && (
          <Box>
            {/* Details Section */}
            <Paper
              sx={{
                p: 3,
                borderRadius: 2,
                mb: 3,
                bgcolor: '#f8f9fa',
                borderLeft: 4,
                borderColor: 'primary.main',
              }}
            >
              <Typography
                variant='h6'
                fontWeight='bold'
                color='primary'
                gutterBottom
              >
                Booking Summary
              </Typography>
              <Divider sx={{ mb: 3 }} />
              <Grid container spacing={3}>
                <Grid item xs={12} md={6} lg={3}>
                  <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 2 }}>
                    <Typography variant='caption' color='text.secondary'>
                      Project Name
                    </Typography>
                    <Typography variant='body1' fontWeight='600'>
                      {bookingInfo?.particular?.projectName ||
                        booking.branchName ||
                        booking.particular?.projectName ||
                        'N/A'}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6} lg={3}>
                  <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 2 }}>
                    <Typography variant='caption' color='text.secondary'>
                      Customer Name
                    </Typography>
                    <Typography variant='body1' fontWeight='600'>
                      {bookingInfo?.customer?.name ||
                        booking.customer ||
                        booking.customerDetails?.name ||
                        'N/A'}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6} lg={3}>
                  <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 2 }}>
                    <Typography variant='caption' color='text.secondary'>
                      Property/Unit No
                    </Typography>
                    <Typography variant='body1' fontWeight='600'>
                      {bookingInfo?.particular?.inventoryDetail ||
                        booking.plotNo ||
                        booking.particular?.inventoryDetail ||
                        'N/A'}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6} lg={3}>
                  <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 2 }}>
                    <Typography variant='caption' color='text.secondary'>
                      Property Amount
                    </Typography>
                    <Typography variant='body1' fontWeight='600'>
                      ₹
                      {(
                        bookingInfo?.propertyAmount ??
                        booking.propertyAmount ??
                        0
                      ).toLocaleString('en-IN')}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6} lg={3}>
                  <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 2 }}>
                    <Typography variant='caption' color='text.secondary'>
                      Booking Amount
                    </Typography>
                    <Typography
                      variant='body1'
                      fontWeight='600'
                      color='primary.main'
                    >
                      ₹
                      {(
                        bookingInfo?.bookingAmount ??
                        booking.bookingAmount ??
                        0
                      ).toLocaleString('en-IN')}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6} lg={3}>
                  <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 2 }}>
                    <Typography variant='caption' color='text.secondary'>
                      Total Received
                    </Typography>
                    <Typography
                      variant='body1'
                      fontWeight='600'
                      color='success.main'
                    >
                      ₹
                      {(
                        bookingInfo?.totalReceived ??
                        booking.totalReceived ??
                        0
                      ).toLocaleString('en-IN')}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6} lg={3}>
                  <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 2 }}>
                    <Typography variant='caption' color='text.secondary'>
                      Discount
                    </Typography>
                    <Typography
                      variant='body1'
                      fontWeight='600'
                      color='info.main'
                    >
                      ₹
                      {(
                        bookingInfo?.discount ??
                        booking.discount ??
                        0
                      ).toLocaleString('en-IN')}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6} lg={3}>
                  <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 2 }}>
                    <Typography variant='caption' color='text.secondary'>
                      Total Due
                    </Typography>
                    <Typography
                      variant='body1'
                      fontWeight='600'
                      color='error.main'
                    >
                      ₹
                      {(
                        bookingInfo?.totalDueAmount ??
                        booking.totalDueAmount ??
                        Math.max(
                          0,
                          (bookingInfo?.propertyAmount ??
                            booking.propertyAmount ??
                            0) -
                          (bookingInfo?.totalReceived ??
                            booking.totalReceived ??
                            0),
                        )
                      ).toLocaleString('en-IN')}
                    </Typography>
                  </Box>
                </Grid>
                {/* <Grid item xs={12} md={6} lg={3}>
                  <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 2 }}>
                    <Typography variant="caption" color="text.secondary">Other</Typography>
                    <Typography variant="body1" fontWeight="600">₹0</Typography>
                  </Box>
                </Grid> */}
                {/* <Grid item xs={12} md={6} lg={3}>
                  <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 2 }}>
                    <Typography variant="caption" color="text.secondary">Other Received</Typography>
                    <Typography variant="body1" fontWeight="600" color="success.main">₹0</Typography>
                  </Box>
                </Grid> */}
                {/* <Grid item xs={12} md={6} lg={3}>
                  <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 2 }}>
                    <Typography variant="caption" color="text.secondary">Other Due</Typography>
                    <Typography variant="body1" fontWeight="600" color="error.main">₹0</Typography>
                  </Box>
                </Grid> */}
              </Grid>
            </Paper>

            {/* Form Section */}
            <Paper sx={{ p: 3, borderRadius: 2, mb: 3 }}>
              <Typography
                variant='h6'
                fontWeight='bold'
                color='primary'
                gutterBottom
              >
                Add New Receipt
              </Typography>
              <Divider sx={{ mb: 3 }} />
              {receiptSaveSuccess && (
                <Alert severity='success' sx={{ mb: 2 }}>
                  Receipt saved successfully!
                </Alert>
              )}
              {receiptSaveError && (
                <Alert severity='error' sx={{ mb: 2 }}>
                  {receiptSaveError}
                </Alert>
              )}
              {canWriteBooking && (
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6} lg={4}>
                    <TextField
                      fullWidth
                      label='Date'
                      type='date'
                      value={receiptForm.receivedDate}
                      onChange={(e) =>
                        handleReceiptFormChange('receivedDate', e.target.value)
                      }
                      InputLabelProps={{ shrink: true }}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={6} lg={4}>
                    <FormControl fullWidth required>
                      <InputLabel>Receipt Type</InputLabel>
                      <Select
                        label='Receipt Type'
                        value={receiptForm.receivedType}
                        onChange={(e) =>
                          handleReceiptFormChange(
                            'receivedType',
                            e.target.value,
                          )
                        }
                      >
                        <MenuItem value='Booking'>Booking </MenuItem>
                        <MenuItem value='installment'>Installment</MenuItem>
                        <MenuItem value='Advance'>Advance Amount</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6} lg={4}>
                    <FormControl fullWidth required>
                      <InputLabel>Payment Type</InputLabel>
                      <Select
                        label='Payment Type'
                        value={receiptForm.paymentType}
                        onChange={(e) =>
                          handleReceiptFormChange('paymentType', e.target.value)
                        }
                      >
                        <MenuItem value='Registration_Amount'>
                          Registration Amount
                        </MenuItem>
                        <MenuItem value='Other'>Other Amount</MenuItem>
                        {/* <MenuItem value="Advance">Advance Amount</MenuItem> */}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6} lg={4}>
                    <FormControl fullWidth required>
                      <InputLabel>Payment Mode</InputLabel>
                      <Select
                        label='Payment Mode'
                        value={receiptForm.mode}
                        onChange={(e) =>
                          handleReceiptFormChange('mode', e.target.value)
                        }
                      >
                        <MenuItem value='Cash'>Cash</MenuItem>
                        <MenuItem value='Cheque'>Cheque</MenuItem>
                        <MenuItem value='RTGS/NEFT'>RTGS/NEFT</MenuItem>
                        <MenuItem value='UPI'>UPI</MenuItem>
                        <MenuItem value='Other'>Other</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6} lg={4}>
                    <TextField
                      fullWidth
                      label='Ref. No'
                      placeholder='Enter reference number'
                      value={receiptForm.refNo}
                      onChange={(e) =>
                        handleReceiptFormChange('refNo', e.target.value)
                      }
                    />
                  </Grid>
                  {renderPaymentModeFields()}
                  <Grid item xs={12} md={6} lg={4}>
                    <TextField
                      fullWidth
                      label='Amount'
                      type='text'
                      value={receiptForm.amount}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^0-9.]/g, ''); // Only allow digits and decimal point
                        handleReceiptFormChange('amount', value);
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position='start'>₹</InputAdornment>
                        ),
                      }}
                      required
                      error={!!amountError}
                      helperText={
                        amountError ||
                        `Max: ₹${(
                          bookingInfo?.totalDueAmount ??
                          booking.totalDueAmount ??
                          Math.max(
                            0,
                            (bookingInfo?.propertyAmount ??
                              booking.propertyAmount ??
                              0) -
                            (bookingInfo?.totalReceived ??
                              booking.totalReceived ??
                              0),
                          )
                        ).toLocaleString('en-IN')}`
                      }
                      sx={{
                        '& input': {
                          MozAppearance: 'textfield',
                        },
                        '& input::-webkit-outer-spin-button': {
                          WebkitAppearance: 'none',
                          margin: 0,
                        },
                        '& input::-webkit-inner-spin-button': {
                          WebkitAppearance: 'none',
                          margin: 0,
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label='Other Remark'
                      placeholder='Enter remarks (optional)'
                      multiline
                      rows={2}
                      value={receiptForm.otherRemark}
                      onChange={(e) =>
                        handleReceiptFormChange('otherRemark', e.target.value)
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    {canWriteBooking && (
                      <Button
                        variant='contained'
                        startIcon={
                          savingReceipt ? (
                            <CircularProgress size={18} color='inherit' />
                          ) : (
                            <Save />
                          )
                        }
                        size='large'
                        sx={{ px: 4 }}
                        onClick={handleSaveReceipt}
                        disabled={savingReceipt}
                      >
                        {savingReceipt ? 'Saving...' : 'Save Receipt'}
                      </Button>
                    )}
                  </Grid>
                </Grid>
              )}
            </Paper>

            {/* List Section */}
            <Paper sx={{ p: 3, borderRadius: 2 }}>
              <Typography
                variant='h6'
                fontWeight='bold'
                color='primary'
                gutterBottom
              >
                Receipt History
              </Typography>
              <Divider sx={{ mb: 3 }} />
              {receiptsLoading ? (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    py: 4,
                  }}
                >
                  <CircularProgress />
                  <Typography
                    variant='body2'
                    color='text.secondary'
                    sx={{ ml: 2 }}
                  >
                    Loading receipts...
                  </Typography>
                </Box>
              ) : receiptsError ? (
                <Alert severity='error' sx={{ mb: 2 }}>
                  {receiptsError}
                </Alert>
              ) : (
                <TableContainer>
                  <Table size='small'>
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                        <TableCell>
                          <strong>Sr No.</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Receipt Date</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Receipt No.</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Receipt Type</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Payment Type</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Mode</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Ref No.</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Bank Name</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Amount</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Other Remark</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Remark</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Status</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Actions</strong>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {receipts.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={13} align='center' sx={{ py: 4 }}>
                            <Typography variant='body2' color='text.secondary'>
                              No receipts found
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        receipts.map((receipt, index) => {
                          const receiptDate = receipt.receivedDate
                            ? new Date(receipt.receivedDate)
                              .toISOString()
                              .split('T')[0]
                            : '-';
                          const receivedType = receipt.receivedType || '-';
                          const paymentType =
                            receipt.paymentType === 'fullPayment'
                              ? 'Full Payment'
                              : receipt.paymentType === 'partialPayment'
                                ? 'Partial Payment'
                                : receipt.paymentType || '-';

                          return (
                            <TableRow key={receipt._id}>
                              <TableCell>{index + 1}</TableCell>
                              <TableCell>
                                {moment(receiptDate).format('Do MMMM YYYY')}
                              </TableCell>
                              <TableCell>{receipt.receiptId || '-'}</TableCell>
                              <TableCell>{receivedType}</TableCell>
                              <TableCell>{paymentType}</TableCell>
                              <TableCell>{receipt.mode || '-'}</TableCell>
                              <TableCell>{receipt.refNo || '-'}</TableCell>
                              <TableCell>{receipt.bankName || '-'}</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>
                                ₹{(receipt.amount || 0).toLocaleString('en-IN')}
                              </TableCell>
                              <TableCell>
                                {receipt.otherRemark || '-'}
                              </TableCell>
                              <TableCell>
                                {receipt.approvalRemark && (
                                  <Box>
                                    <Typography
                                      variant='caption'
                                      color='success.main'
                                      sx={{ display: 'block', fontWeight: 600 }}
                                    >
                                      Approval:
                                    </Typography>
                                    {receipt.approvalRemark}
                                  </Box>
                                )}
                                {receipt.cancelRemark && (
                                  <Box
                                    sx={{ mt: receipt.approvalRemark ? 1 : 0 }}
                                  >
                                    <Typography
                                      variant='caption'
                                      color='error'
                                      sx={{ display: 'block', fontWeight: 600 }}
                                    >
                                      Cancel:
                                    </Typography>
                                    {receipt.cancelRemark}
                                  </Box>
                                )}
                                {!receipt.approvalRemark &&
                                  !receipt.cancelRemark &&
                                  '-'}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={receipt.receiptStatus || 'pending'}
                                  size='small'
                                  color={
                                    receipt.receiptStatus === 'approved'
                                      ? 'success'
                                      : receipt.receiptStatus === 'cancelled'
                                        ? 'error'
                                        : 'warning'
                                  }
                                  sx={{
                                    textTransform: 'capitalize',
                                    fontWeight: 600,
                                  }}
                                />
                                {receipt.receiptStatus === 'approved' &&
                                  receipt.approvedAt && (
                                    <Box sx={{ mt: 0.5 }}>
                                      <Typography
                                        variant='caption'
                                        display='block'
                                        color='text.secondary'
                                      >
                                        Approve:{' '}
                                        {moment(receipt.approvedAt).format(
                                          'DD-MM-YYYY',
                                        )}
                                      </Typography>
                                    </Box>
                                  )}
                                {receipt.receiptStatus === 'cancelled' &&
                                  receipt.cancelledAt && (
                                    <Box sx={{ mt: 0.5 }}>
                                      <Typography
                                        variant='caption'
                                        display='block'
                                        color='text.secondary'
                                      >
                                        Cancel:{' '}
                                        {moment(receipt.cancelledAt).format(
                                          'DD-MM-YYYY',
                                        )}
                                      </Typography>
                                    </Box>
                                  )}
                              </TableCell>
                              <TableCell>
                                {receipt.receiptStatus !== 'cancelled' &&
                                  receipt.receiptStatus !== 'pending' && (
                                    <>
                                      <IconButton
                                        size='small'
                                        color='info'
                                        title='Download'
                                        onClick={() => {
                                          setSelectedReceipt(receipt);
                                          setPdfType('receipt');
                                          setPdfDialogOpen(true);
                                        }}
                                      >
                                        <Download fontSize='small' />
                                      </IconButton>
                                      {isEditMode && (
                                        <IconButton
                                          size='small'
                                          color='error'
                                          title='Cancel'
                                          onClick={() =>
                                            handleDeleteReceipt(receipt)
                                          }
                                          disabled={
                                            deletingReceiptId === receipt._id
                                          }
                                        >
                                          {deletingReceiptId === receipt._id ? (
                                            <CircularProgress size={16} />
                                          ) : (
                                            <CancelIcon fontSize='small' />
                                          )}
                                        </IconButton>
                                      )}
                                    </>
                                  )}
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          </Box>
        )}

        {/* Tab 3: Document */}
        {activeTabLabel === 'Document' && (
          <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Typography
              variant='h6'
              fontWeight='bold'
              color='primary'
              gutterBottom
            >
              Document Management
            </Typography>
            <Divider sx={{ mb: 3 }} />
            <Grid container spacing={3}>
              <Grid item xs={12}>
                {isEditMode && (
                  <Paper
                    sx={{
                      p: 3,
                      border: '2px dashed #ccc',
                      mb: 3,
                      bgcolor: '#f8f9fa',
                    }}
                  >
                    <Typography
                      variant='subtitle1'
                      fontWeight='bold'
                      gutterBottom
                    >
                      Upload New Document
                    </Typography>
                    {documentUploadSuccess && (
                      <Alert
                        severity='success'
                        sx={{ mb: 2 }}
                        onClose={() => setDocumentUploadSuccess(false)}
                      >
                        Document uploaded successfully!
                      </Alert>
                    )}
                    {documentUploadError && (
                      <Alert
                        severity='error'
                        sx={{ mb: 2 }}
                        onClose={() => setDocumentUploadError('')}
                      >
                        {documentUploadError}
                      </Alert>
                    )}
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}>
                        <FormControl fullWidth required>
                          <InputLabel>Document Type</InputLabel>
                          <Select
                            label='Document Type'
                            value={documentUpload.type}
                            onChange={(e) => {
                              setDocumentUpload((prev) => ({
                                ...prev,
                                type: e.target.value,
                                name: '',
                              }));
                              setDocumentUploadError('');
                            }}
                          >
                            <MenuItem value='aadhar'>Aadhar Card</MenuItem>
                            <MenuItem value='pan'>PAN Card</MenuItem>
                            <MenuItem value='agreement'>Agreement</MenuItem>
                            <MenuItem value='paymentReceipt'>
                              Payment Receipt
                            </MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          label='Document Name'
                          placeholder='e.g., Aadhar Card Front'
                          value={documentUpload.name}
                          onChange={(e) =>
                            setDocumentUpload((prev) => ({
                              ...prev,
                              name: e.target.value,
                            }))
                          }
                          disabled={!documentUpload.type}
                          helperText={
                            !documentUpload.type
                              ? 'Select document type first'
                              : ''
                          }
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Button
                          variant='outlined'
                          component='label'
                          fullWidth
                          startIcon={<Upload />}
                          disabled={!documentUpload.type || uploadingDocument}
                          sx={{ height: '56px' }}
                        >
                          {documentUpload.file
                            ? documentUpload.file.name
                            : 'Choose File'}
                          <input
                            type='file'
                            hidden
                            accept='image/*,.pdf'
                            onChange={(e) => {
                              const file = e.target.files[0];
                              if (file) {
                                // Check file size (max 10MB)
                                if (file.size > 10 * 1024 * 1024) {
                                  setDocumentUploadError(
                                    'File size must be less than 10MB',
                                  );
                                  return;
                                }
                                setDocumentUpload((prev) => ({
                                  ...prev,
                                  file,
                                }));
                                setDocumentUploadError('');
                                // Auto-generate name if not provided
                                if (!documentUpload.name) {
                                  const typeNames = {
                                    aadhar: 'Aadhar Card',
                                    pan: 'PAN Card',
                                    agreement: 'Agreement',
                                    paymentReceipt: 'Payment Receipt',
                                  };
                                  setDocumentUpload((prev) => ({
                                    ...prev,
                                    name: typeNames[prev.type] || 'Document',
                                  }));
                                }
                              }
                            }}
                          />
                        </Button>
                      </Grid>
                      <Grid item xs={12}>
                        {canWriteBooking && (
                          <Button
                            variant='contained'
                            startIcon={
                              uploadingDocument ? (
                                <CircularProgress size={18} color='inherit' />
                              ) : (
                                <Upload />
                              )
                            }
                            onClick={handleUploadDocument}
                            disabled={
                              !documentUpload.type ||
                              !documentUpload.file ||
                              !documentUpload.name ||
                              uploadingDocument
                            }
                            sx={{ mt: 1 }}
                          >
                            {uploadingDocument
                              ? 'Uploading...'
                              : 'Upload Document'}
                          </Button>
                        )}
                      </Grid>
                    </Grid>
                  </Paper>
                )}
                <Typography variant='subtitle2' fontWeight='bold' gutterBottom>
                  Uploaded Documents
                </Typography>
                {documentsLoading ? (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      py: 4,
                    }}
                  >
                    <CircularProgress />
                    <Typography
                      variant='body2'
                      color='text.secondary'
                      sx={{ ml: 2 }}
                    >
                      Loading documents...
                    </Typography>
                  </Box>
                ) : documentsError ? (
                  <Alert severity='error' sx={{ mb: 2 }}>
                    {documentsError}
                  </Alert>
                ) : documents.length === 0 ? (
                  <Paper sx={{ p: 3, textAlign: 'center', bgcolor: '#f8f9fa' }}>
                    <Typography variant='body2' color='text.secondary'>
                      No documents uploaded yet.
                    </Typography>
                  </Paper>
                ) : (
                  <Grid container spacing={2}>
                    {documents.map((doc, idx) => (
                      <Grid item xs={12} md={6} key={doc._id || idx}>
                        <Paper
                          sx={{
                            p: 2,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                          }}
                        >
                          <Description color='primary' />
                          <Box sx={{ flex: 1 }}>
                            <Typography variant='body2' fontWeight='600'>
                              {doc.name || doc.type || 'Document'}
                            </Typography>
                            <Typography
                              variant='caption'
                              color='text.secondary'
                              display='block'
                            >
                              Type: {doc.type || 'N/A'}
                            </Typography>
                            <Typography
                              variant='caption'
                              color='text.secondary'
                              display='block'
                            >
                              Uploaded:{' '}
                              {doc.uploadedAt
                                ? new Date(doc.uploadedAt).toLocaleDateString()
                                : 'N/A'}
                              {doc.uploadedBy && ` by ${doc.uploadedBy}`}
                            </Typography>
                          </Box>
                          <IconButton
                            size='small'
                            color='primary'
                            title='Download'
                            onClick={() => handleDownloadDocument(doc)}
                          >
                            <Download fontSize='small' />
                          </IconButton>
                          {isEditMode && (
                            <IconButton
                              size='small'
                              color='error'
                              title='Delete'
                              onClick={() => handleDeleteDocument(doc)}
                            >
                              <Delete fontSize='small' />
                            </IconButton>
                          )}
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Grid>
            </Grid>
          </Paper>
        )}

        {/* Tab 4: Followup/Remark */}
        {activeTabLabel === 'Followup/Remark' && (
          <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Typography
              variant='h6'
              fontWeight='bold'
              color='primary'
              gutterBottom
            >
              Followup & Remarks
            </Typography>
            <Divider sx={{ mb: 3 }} />
            <Grid container spacing={3}>
              {isEditMode && (
                <Grid item xs={12}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <Paper
                      sx={{
                        p: 3,
                        mb: 4,
                        bgcolor: '#fcfcfc',
                        border: '1px solid #e0e0e0',
                        borderRadius: 2,
                      }}
                    >
                      <Typography
                        variant='subtitle1'
                        fontWeight='bold'
                        gutterBottom
                        color='primary'
                      >
                        Add New Follow-up
                      </Typography>

                      {followUpSaveSuccess && (
                        <Alert
                          severity='success'
                          sx={{ mb: 2 }}
                          onClose={() => setFollowUpSaveSuccess('')}
                        >
                          {followUpSaveSuccess}
                        </Alert>
                      )}
                      {followUpSaveError && (
                        <Alert
                          severity='error'
                          sx={{ mb: 2 }}
                          onClose={() => setFollowUpSaveError('')}
                        >
                          {followUpSaveError}
                        </Alert>
                      )}

                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={3}>
                          <DateTimePicker
                            label='Follow-up Date'
                            value={new Date(followUpForm.date)}
                            onChange={(newValue) =>
                              handleFollowUpFormChange(
                                'date',
                                newValue?.toISOString(),
                              )
                            }
                            renderInput={(params) => (
                              <TextField {...params} fullWidth size='small' />
                            )}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <DateTimePicker
                            label='Next Follow-up Date'
                            value={
                              followUpForm.nextFollowUpDate
                                ? new Date(followUpForm.nextFollowUpDate)
                                : null
                            }
                            onChange={(newValue) =>
                              handleFollowUpFormChange(
                                'nextFollowUpDate',
                                newValue?.toISOString(),
                              )
                            }
                            minDate={new Date()}
                            renderInput={(params) => (
                              <TextField {...params} fullWidth size='small' />
                            )}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <FormControl fullWidth size='small'>
                            <InputLabel>Status</InputLabel>
                            <Select
                              label='Status'
                              value={followUpForm.status}
                              onChange={(e) =>
                                handleFollowUpFormChange(
                                  'status',
                                  e.target.value,
                                )
                              }
                            >
                              {inquiryStatuses.map((status) => (
                                <MenuItem
                                  key={status.id}
                                  value={status.statusName}
                                >
                                  {status.statusName}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid
                          item
                          xs={12}
                          sm={6}
                          md={3}
                          sx={{ display: 'flex', alignItems: 'center' }}
                        >
                          <Button
                            variant='contained'
                            fullWidth
                            startIcon={
                              savingFollowUp ? (
                                <CircularProgress size={20} color='inherit' />
                              ) : (
                                <Save />
                              )
                            }
                            onClick={handleSaveFollowUp}
                            disabled={savingFollowUp}
                          >
                            {savingFollowUp ? 'Saving...' : 'Save Follow-up'}
                          </Button>
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            multiline
                            rows={2}
                            label='Remark'
                            placeholder='Enter follow-up details...'
                            value={followUpForm.remark}
                            onChange={(e) =>
                              handleFollowUpFormChange('remark', e.target.value)
                            }
                          />
                        </Grid>
                      </Grid>
                    </Paper>
                  </LocalizationProvider>
                </Grid>
              )}
              <Grid item xs={12}>
                <Typography variant='subtitle2' fontWeight='bold' gutterBottom>
                  Previous Remarks
                </Typography>
                {followUpsLoading ? (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      py: 4,
                    }}
                  >
                    <CircularProgress size={24} />
                    <Typography
                      variant='body2'
                      color='text.secondary'
                      sx={{ ml: 2 }}
                    >
                      Loading follow-ups...
                    </Typography>
                  </Box>
                ) : followUpsError ? (
                  <Alert severity='error' sx={{ mb: 2 }}>
                    {followUpsError}
                  </Alert>
                ) : followUps.length === 0 ? (
                  <Paper sx={{ p: 3, textAlign: 'center', bgcolor: '#f8f9fa' }}>
                    <Typography variant='body2' color='text.secondary'>
                      No follow-ups found for this booking.
                    </Typography>
                  </Paper>
                ) : (
                  <TableContainer
                    component={Paper}
                    sx={{
                      boxShadow: 'none',
                      border: '1px solid #f0f0f0',
                      borderRadius: 2,
                    }}
                  >
                    <Table size='small'>
                      <TableHead sx={{ bgcolor: '#f8f9fa' }}>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold' }}>
                            Followup Date
                          </TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>
                            Next Followup Date
                          </TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>
                            Status
                          </TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>
                            Remark
                          </TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>
                            Created By
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {followUps.map((followUp, idx) => {
                          const fDate =
                            followUp.followUpDate ||
                            followUp.date ||
                            followUp.createdAt ||
                            followUp.createdOn;
                          const formattedFollowUpDate = fDate
                            ? new Date(fDate).toLocaleString('en-IN', {
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                              hour12: true,
                            })
                            : '-';

                          const nextFDate = followUp.nextFollowUpDate;
                          const formattedNextDate = nextFDate
                            ? new Date(nextFDate).toLocaleString('en-IN', {
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric',
                            })
                            : '-';

                          const status =
                            followUp.status || followUp.type || '-';
                          const remark =
                            followUp.remark || followUp.description || '-';
                          const createdBy =
                            followUp.addedBy ||
                            followUp.createdBy ||
                            followUp.createdByUser ||
                            'Admin';

                          return (
                            <TableRow key={followUp._id || idx} hover>
                              <TableCell sx={{ fontSize: '0.875rem' }}>
                                {formattedFollowUpDate}
                              </TableCell>
                              <TableCell sx={{ fontSize: '0.875rem' }}>
                                {formattedNextDate}
                              </TableCell>
                              <TableCell sx={{ fontSize: '0.875rem' }}>
                                <Chip
                                  label={status}
                                  size='small'
                                  sx={{
                                    fontWeight: 600,
                                    fontSize: '0.75rem',
                                    bgcolor:
                                      status.toUpperCase() === 'HOT'
                                        ? '#ffebee'
                                        : '#e3f2fd',
                                    color:
                                      status.toUpperCase() === 'HOT'
                                        ? '#c62828'
                                        : '#1565c0',
                                  }}
                                />
                              </TableCell>
                              <TableCell
                                sx={{
                                  fontSize: '0.875rem',
                                  maxWidth: 300,
                                  wordBreak: 'break-word',
                                }}
                              >
                                {remark}
                              </TableCell>
                              <TableCell sx={{ fontSize: '0.875rem' }}>
                                {createdBy}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                    <TablePagination
                      rowsPerPageOptions={[5, 10, 25]}
                      component='div'
                      count={followUpsTotalCount}
                      rowsPerPage={followUpsRowsPerPage}
                      page={followUpsPage}
                      onPageChange={(e, newPage) => setFollowUpsPage(newPage)}
                      onRowsPerPageChange={(e) => {
                        setFollowUpsRowsPerPage(parseInt(e.target.value, 10));
                        setFollowUpsPage(0);
                      }}
                    />
                  </TableContainer>
                )}
              </Grid>
            </Grid>
          </Paper>
        )}

        {/* Tab 5: Commission */}
        {activeTabLabel === 'Commission' && (
          <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Typography variant='h6' fontWeight='bold' color='primary'>
                  Commission Details
                </Typography>
                {isCommissionTypeFixed && (
                  <Chip
                    label='Commission Fixed'
                    color='success'
                    size='small'
                    sx={{
                      fontWeight: 700,
                      letterSpacing: 0.5,
                      borderRadius: 1.5,
                      boxShadow: '0 2px 6px rgba(76, 175, 80, 0.25)',
                    }}
                  />
                )}
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                <Typography
                  variant='subtitle1'
                  fontWeight='bold'
                  color='text.secondary'
                >
                  Total Distribution Amount: ₹{totalDistribution.toLocaleString('en-IN')}
                </Typography>
                <Typography variant='h6' fontWeight='bold' color='success.main'>
                  Total Payable: ₹{totalCommission.toLocaleString('en-IN')}
                </Typography>
              </Box>
            </Box>
            <Divider sx={{ mb: 3 }} />

            {/* Display Fixed Commission Remark if present */}
            {Boolean(fixedCommissionRemark && String(fixedCommissionRemark).trim()) && (
              <Alert
                severity='info'
                sx={{
                  mb: 3,
                  borderRadius: 2,
                  bgcolor: 'rgba(25, 118, 210, 0.06)',
                  border: '1px solid rgba(25, 118, 210, 0.2)',
                  '& .MuiAlert-icon': { fontSize: 22, color: 'primary.main' }
                }}
              >
                <Typography variant='subtitle2' sx={{ fontWeight: 700, color: 'primary.main' }}>
                  Fixed Commission Remark:
                </Typography>
                <Typography variant='body2' sx={{ fontWeight: 500, mt: 0.3, color: 'text.primary' }}>
                  {fixedCommissionRemark}
                </Typography>
              </Alert>
            )}
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Box
                  sx={{
                    mb: 3,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 2,
                  }}
                >
                  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Autocomplete
                      sx={{ width: { xs: '100%', sm: 300 } }}
                      size='small'
                      options={associatesList}
                      loading={associatesLoading}
                      getOptionLabel={(option) => {
                        const name = option.name || '';
                        const id = option.associateId || option._id || '';
                        return id ? `${name} (${id})` : name;
                      }}
                      isOptionEqualToValue={(option, value) =>
                        (option._id || option.id) === (value?._id || value?.id)
                      }
                      value={commissionsAssociateFilter}
                      onChange={(event, newValue) => {
                        setCommissionsAssociateFilter(newValue);
                        setCommissionsPage(0); // Reset to first page on filter change
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label='Filter by Associate'
                          placeholder='Search associate...'
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <InputAdornment position='start'>
                                <Search
                                  sx={{ fontSize: 20, color: 'text.secondary' }}
                                />
                              </InputAdornment>
                            ),
                            endAdornment: (
                              <React.Fragment>
                                {associatesLoading ? (
                                  <CircularProgress color='inherit' size={20} />
                                ) : null}
                                {params.InputProps.endAdornment}
                              </React.Fragment>
                            ),
                          }}
                        />
                      )}
                    />
                    {commissionsAssociateFilter && (
                      <Button
                        size='small'
                        onClick={() => {
                          setCommissionsAssociateFilter(null);
                          setCommissionsPage(0);
                        }}
                      >
                        Clear Filter
                      </Button>
                    )}
                  </Box>

                  {/* View Mode Switcher: Receipts View vs Grouped View */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant='body2' sx={{ fontWeight: 600, color: 'text.secondary' }}>
                      View Mode:
                    </Typography>
                    <ToggleButtonGroup
                      size='small'
                      value={commissionViewMode}
                      exclusive
                      onChange={(e, newMode) => {
                        if (newMode) setCommissionViewMode(newMode);
                      }}
                      sx={{
                        bgcolor: 'background.paper',
                        boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                        '& .MuiToggleButton-root': {
                          px: 1.8,
                          py: 0.6,
                          textTransform: 'none',
                          fontWeight: 600,
                          fontSize: '0.8125rem',
                          '&.Mui-selected': {
                            bgcolor: 'primary.main',
                            color: '#fff',
                            '&:hover': {
                              bgcolor: 'primary.dark',
                            },
                          },
                        },
                      }}
                    >
                      <ToggleButton value='detailed'>
                        <ReceiptLong sx={{ fontSize: 17, mr: 0.8 }} />
                        Receipts View
                      </ToggleButton>
                      <ToggleButton value='grouped'>
                        <Groups sx={{ fontSize: 18, mr: 0.8 }} />
                        Grouped View
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </Box>
                </Box>

                {commissionViewMode === 'grouped' ? (
                  <TableContainer component={Paper} variant='outlined'>
                    <Table size='small'>
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                          <TableCell sx={{ width: 45 }} />
                          <TableCell><strong>Associate</strong></TableCell>
                          <TableCell><strong>Project / Unit</strong></TableCell>
                          <TableCell align='center'><strong>Commission %</strong></TableCell>
                          {/* <TableCell align='right'><strong>Total Received</strong></TableCell> */}
                          <TableCell align='right'><strong>Total Payable</strong></TableCell>
                          {/* <TableCell align='right'><strong>Paid</strong></TableCell> */}
                          {/* <TableCell align='right'><strong>Due</strong></TableCell> */}
                          <TableCell align='center'><strong>Receipts</strong></TableCell>
                          {/* <TableCell align='center'><strong>Status</strong></TableCell> */}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {commissionsLoading && (
                          <TableRow>
                            <TableCell colSpan={10} align='center' sx={{ py: 4 }}>
                              <CircularProgress size={24} />
                            </TableCell>
                          </TableRow>
                        )}
                        {!commissionsLoading && commissionsError && (
                          <TableRow>
                            <TableCell colSpan={10} align='center' sx={{ color: 'error.main', py: 3 }}>
                              {commissionsError}
                            </TableCell>
                          </TableRow>
                        )}
                        {!commissionsLoading && !commissionsError && commissions.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={10} align='center' sx={{ py: 3, color: 'text.secondary' }}>
                              No grouped commission records found for this booking.
                            </TableCell>
                          </TableRow>
                        )}
                        {!commissionsLoading && !commissionsError && commissions.map((row) => {
                          const isExpanded = Boolean(expandedGroupedRows[row._id]);
                          const receiptsList = Array.isArray(row.receipts) ? row.receipts : [];

                          return (
                            <React.Fragment key={row._id}>
                              <TableRow hover sx={{ '& > *': { borderBottom: 'unset' } }}>
                                <TableCell>
                                  {receiptsList.length > 0 && (
                                    <IconButton
                                      aria-label='expand row'
                                      size='small'
                                      onClick={() => toggleRowExpansion(row._id)}
                                    >
                                      {isExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                                    </IconButton>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <Typography variant='body2' fontWeight='bold'>
                                    {row.associate || '-'}
                                  </Typography>
                                  <Typography variant='caption' display='block' color='text.secondary'>
                                    {row.associatePhone ? `${row.associatePhone} • ` : ''}
                                    {row.associateDesignation || row.associateRole || 'Associate'}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant='body2'>
                                    {row.project || '-'}
                                  </Typography>
                                  <Typography variant='caption' display='block' color='text.secondary'>
                                    {row.unitType} {row.unitDetail ? `- ${row.unitDetail}` : ''}
                                  </Typography>
                                </TableCell>
                                <TableCell align='center'>
                                  <Chip
                                    size='small'
                                    label={`${row.payableCommissionPercent}%`}
                                    color='primary'
                                    variant='outlined'
                                    sx={{ fontWeight: 700 }}
                                  />
                                </TableCell>
                                {/* <TableCell align='right'>
                                  ₹{(row.receivedAmount || row.paymentAmount || 0).toLocaleString('en-IN')}
                                </TableCell> */}
                                <TableCell align='right' sx={{ color: 'success.main', fontWeight: 'bold' }}>
                                  ₹{(row.payableCommissionAmount || 0).toLocaleString('en-IN')}
                                </TableCell>
                                {/* <TableCell align='right' sx={{ color: 'primary.main', fontWeight: 600 }}>
                                  ₹{(row.paidAmount || 0).toLocaleString('en-IN')}
                                </TableCell>
                                <TableCell align='right' sx={{ color: (row.dueCommissionAmount || 0) > 0 ? 'warning.dark' : 'text.secondary', fontWeight: 600 }}>
                                  ₹{(row.dueCommissionAmount || 0).toLocaleString('en-IN')}
                                </TableCell> */}
                                <TableCell align='center'>
                                  <Chip
                                    size='small'
                                    label={`${row.receiptCount || receiptsList.length} Receipts`}
                                    onClick={() => toggleRowExpansion(row._id)}
                                    color='info'
                                    variant='filled'
                                    sx={{ fontWeight: 600, cursor: 'pointer', fontSize: '0.75rem' }}
                                  />
                                </TableCell>
                                {/* <TableCell align='center'>
                                  <Chip
                                    size='small'
                                    label={row.allApproved ? 'Approved' : row.anyApproved ? 'Partially Approved' : 'Pending'}
                                    color={row.allApproved ? 'success' : row.anyApproved ? 'warning' : 'default'}
                                    variant='filled'
                                    sx={{ fontWeight: 700, fontSize: '0.75rem' }}
                                  />
                                </TableCell> */}
                              </TableRow>

                              {/* Collapsible Sub-Table for Receipts */}
                              <TableRow>
                                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={10}>
                                  <Collapse in={isExpanded} timeout='auto' unmountOnExit>
                                    <Box sx={{ margin: 2, p: 2, bgcolor: '#fcfcfc', borderRadius: 2, border: '1px solid #e0e0e0' }}>
                                      <Typography variant='subtitle2' fontWeight='bold' color='primary' gutterBottom>
                                        Receipts Breakdown for {row.associate}
                                      </Typography>
                                      <Table size='small'>
                                        <TableHead>
                                          <TableRow sx={{ bgcolor: '#eee' }}>
                                            <TableCell><strong>Receipt No.</strong></TableCell>
                                            <TableCell><strong>Received Date</strong></TableCell>
                                            <TableCell align='right'><strong>Received Amount</strong></TableCell>
                                            <TableCell align='center'><strong>Comm. %</strong></TableCell>
                                            <TableCell align='right'><strong>Payable Commission</strong></TableCell>
                                            {/* <TableCell align='center'><strong>Status</strong></TableCell> */}
                                          </TableRow>
                                        </TableHead>
                                        <TableBody>
                                          {receiptsList.map((rcp, rIdx) => (
                                            <TableRow key={rcp.receiptId || rcp._id || rIdx} hover>
                                              <TableCell>
                                                <Typography variant='body2' fontWeight='bold' color='primary'>
                                                  {rcp.receiptNo || '-'}
                                                </Typography>
                                              </TableCell>
                                              <TableCell>
                                                {rcp.receivedDate ? new Date(rcp.receivedDate).toLocaleDateString('en-IN') : '-'}
                                              </TableCell>
                                              <TableCell align='right'>
                                                ₹{(rcp.receivedAmount || rcp.paymentAmount || 0).toLocaleString('en-IN')}
                                              </TableCell>
                                              <TableCell align='center'>
                                                {rcp.payableCommissionPercent}%
                                              </TableCell>
                                              <TableCell align='right' sx={{ color: 'success.main', fontWeight: 'bold' }}>
                                                ₹{(rcp.payableCommissionAmount || 0).toLocaleString('en-IN')}
                                              </TableCell>
                                              {/* <TableCell align='center'>
                                                <Chip
                                                  size='small'
                                                  label={rcp.isApproved ? 'Approved' : 'Pending'}
                                                  color={rcp.isApproved ? 'success' : 'default'}
                                                  variant='outlined'
                                                  sx={{ fontWeight: 600, fontSize: '0.7rem' }}
                                                />
                                              </TableCell> */}
                                            </TableRow>
                                          ))}
                                        </TableBody>
                                      </Table>
                                    </Box>
                                  </Collapse>
                                </TableCell>
                              </TableRow>
                            </React.Fragment>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (

                  <TableContainer component={Paper} variant='outlined'>
                    <Table size='small'>
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                          <TableCell>
                            <strong>Associate</strong>
                          </TableCell>
                          <TableCell>
                            <strong>Project Details</strong>
                          </TableCell>
                          <TableCell>
                            <strong>Booking Details</strong>
                          </TableCell>
                          <TableCell>
                            <strong>Receipt No.</strong>
                          </TableCell>
                          <TableCell>
                            <strong>Received Amount</strong>
                          </TableCell>
                          <TableCell>
                            <strong>Distribution Amount</strong>
                          </TableCell>
                          <TableCell>
                            <strong>Comm. %</strong>
                          </TableCell>
                          <TableCell>
                            <strong>Payable Comm.</strong>
                          </TableCell>
                          {/* <TableCell><strong>Prev. Payable</strong></TableCell> */}
                          {/* <TableCell><strong>Status</strong></TableCell> */}

                          <TableCell>
                            <strong>Date</strong>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {commissionsLoading && (
                          <TableRow>
                            <TableCell colSpan={9} align='center'>
                              <CircularProgress size={20} />
                            </TableCell>
                          </TableRow>
                        )}
                        {!commissionsLoading && commissionsError && (
                          <TableRow>
                            <TableCell
                              colSpan={9}
                              align='center'
                              sx={{ color: 'error.main' }}
                            >
                              {commissionsError}
                            </TableCell>
                          </TableRow>
                        )}
                        {!commissionsLoading &&
                          !commissionsError &&
                          commissions.length === 0 && (
                            <TableRow>
                              <TableCell colSpan={9} align='center'>
                                No commission records found for this booking.
                              </TableCell>
                            </TableRow>
                          )}
                        {!commissionsLoading &&
                          !commissionsError &&
                          (() => {
                            const sortedCommissions = [...commissions].sort(
                              (a, b) => {
                                const idA = String(
                                  a.receiptNo || '',
                                ).toLowerCase();
                                const idB = String(
                                  b.receiptNo || '',
                                ).toLowerCase();
                                if (idA === idB) {
                                  return (
                                    new Date(b.createdAt || 0) -
                                    new Date(a.createdAt || 0)
                                  );
                                }
                                return idB.localeCompare(idA);
                              },
                            );

                            let colorFlag = false;
                            let lastReceiptNo = null;

                            return sortedCommissions.map((row) => {
                              const currentReceiptNo = String(
                                row.receiptNo || '',
                              ).toLowerCase();
                              if (currentReceiptNo !== lastReceiptNo) {
                                colorFlag = !colorFlag;
                                lastReceiptNo = currentReceiptNo;
                              }
                              const bgColor = colorFlag ? '#ffffff' : '#f0f7ff';

                              return (
                                <TableRow
                                  key={row._id}
                                  sx={{
                                    backgroundColor: bgColor,
                                    borderLeft:
                                      currentReceiptNo && currentReceiptNo !== '-'
                                        ? '4px solid #3b82f6'
                                        : 'none',
                                    transition: 'all 0.2s',
                                  }}
                                >
                                  <TableCell>
                                    <Typography variant='body2' fontWeight='bold'>
                                      {row.associate || '-'}
                                    </Typography>
                                    <Typography
                                      variant='caption'
                                      display='block'
                                      color='text.secondary'
                                    >
                                      {row.associatePhone || '-'}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant='body2'>
                                      {row.project || '-'}
                                    </Typography>
                                    <Typography
                                      variant='caption'
                                      display='block'
                                      color='text.secondary'
                                    >
                                      {row.unitType} - {row.unitDetail}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Typography
                                      variant='caption'
                                      display='block'
                                      color='text.secondary'
                                    >
                                      ID: {row.bookingNo}
                                    </Typography>
                                    <Typography
                                      variant='caption'
                                      display='block'
                                      color='text.secondary'
                                    >
                                      Size: {row.unitSize}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Typography
                                      variant='body2'
                                      fontWeight='bold'
                                      color='primary'
                                    >
                                      {row.receiptNo || '-'}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    ₹
                                    {(row.receivedAmount || 0).toLocaleString(
                                      'en-IN',
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    ₹
                                    {(row.paymentAmount || 0).toLocaleString(
                                      'en-IN',
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    {row.payableCommissionPercent}%
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      color: 'success.main',
                                      fontWeight: 'bold',
                                    }}
                                  >
                                    ₹
                                    {(
                                      row.payableCommissionAmount || 0
                                    ).toLocaleString('en-IN')}
                                  </TableCell>
                                  {/* <TableCell>
                                ₹{(row.previousPayableCommissionAmount || 0).toLocaleString('en-IN')}
                              </TableCell> */}
                                  {/* <TableCell>
                                <Chip
                                  label={row.isApproved ? 'Approved' : 'Pending'}
                                  color={row.isApproved ? 'success' : 'warning'}
                                  size="small"
                                  variant="outlined"
                                />
                              </TableCell> */}
                                  <TableCell>
                                    {row.createdAt
                                      ? new Date(
                                        row.createdAt,
                                      ).toLocaleDateString('en-IN')
                                      : '-'}
                                  </TableCell>
                                </TableRow>
                              );
                            });
                          })()}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
                <TablePagination
                  component='div'
                  count={commissionsTotalCount}
                  page={commissionsPage}
                  onPageChange={handleChangePage}
                  rowsPerPage={commissionsLimit}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[5, 10, 25, 50]}
                />
              </Grid>
            </Grid>
          </Paper>
        )}

        {/* Tab 6: Progress */}
        {activeTabLabel === 'Progress' && (
          <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Typography
              variant='h6'
              fontWeight='bold'
              color='primary'
              gutterBottom
            >
              Booking Progress Timeline
            </Typography>
            <Divider sx={{ mb: 3 }} />
            {progressLoading ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  py: 4,
                }}
              >
                <CircularProgress />
                <Typography
                  variant='body2'
                  color='text.secondary'
                  sx={{ ml: 2 }}
                >
                  Loading progress...
                </Typography>
              </Box>
            ) : progressError ? (
              <Alert severity='error' sx={{ mb: 2 }}>
                {progressError}
              </Alert>
            ) : progressData.length === 0 ? (
              <Paper sx={{ p: 3, textAlign: 'center', bgcolor: '#f8f9fa' }}>
                <Typography variant='body2' color='text.secondary'>
                  No progress data available for this booking.
                </Typography>
              </Paper>
            ) : (
              <Box sx={{ position: 'relative', pl: 4 }}>
                {progressData.map((item, idx) => {
                  // Map API status to display status
                  const status = (
                    item.status ||
                    item.progressStatus ||
                    'pending'
                  ).toLowerCase();
                  const isCompleted =
                    status === 'completed' ||
                    status === 'done' ||
                    status === 'finished';
                  const isInProgress =
                    status === 'in-progress' ||
                    status === 'in_progress' ||
                    status === 'processing';
                  const isPending = !isCompleted && !isInProgress;

                  // Format date
                  const progressDate =
                    item.createdAt ||
                    item.updatedAt ||
                    item.date ||
                    item.timestamp;
                  const formattedDate = progressDate
                    ? new Date(progressDate).toLocaleString('en-US', {
                      year: 'numeric',
                      month: '2-digit',
                      day: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit',
                    })
                    : 'Date not available';

                  // Get title from progressStage, fallback to other fields
                  const title =
                    item.progressStage ||
                    item.title ||
                    item.stage ||
                    item.milestone ||
                    `Progress ${idx + 1}`;
                  // Get description directly from API
                  const description =
                    item.description ||
                    item.remark ||
                    item.notes ||
                    item.details ||
                    'No description available';
                  // Get addedBy for display
                  const addedBy = item.addedBy || item.createdBy || '';

                  return (
                    <Box
                      key={item._id || idx}
                      sx={{ mb: 3, position: 'relative' }}
                    >
                      <Box
                        sx={{
                          position: 'absolute',
                          left: -20,
                          top: 0,
                          width: 16,
                          height: 16,
                          borderRadius: '50%',
                          bgcolor: '#22c55e',
                          border: '3px solid white',
                          boxShadow: '0 0 0 3px rgba(34,197,94,0.2)',
                        }}
                      />
                      {idx < progressData.length - 1 && (
                        <Box
                          sx={{
                            position: 'absolute',
                            left: -13,
                            top: 16,
                            width: 2,
                            height: 50,
                            bgcolor: '#22c55e',
                          }}
                        />
                      )}
                      <Paper sx={{ p: 2, bgcolor: '#f8f9fa' }}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            mb: 0.5,
                          }}
                        >
                          <Typography variant='body1' fontWeight='600'>
                            {title}
                          </Typography>
                          {isCompleted && (
                            <CheckCircleOutline
                              color='success'
                              fontSize='small'
                            />
                          )}
                          {isInProgress && (
                            <WatchLater color='warning' fontSize='small' />
                          )}
                        </Box>
                        <Typography
                          variant='caption'
                          color='text.secondary'
                          display='block'
                        >
                          {formattedDate}
                          {addedBy && ` • By ${addedBy}`}
                        </Typography>
                        <Typography
                          variant='body2'
                          color='text.secondary'
                          sx={{ mt: 1 }}
                        >
                          {description}
                        </Typography>
                      </Paper>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Paper>
        )}

        {/* Tab 7: Booking Status */}
        {activeTabLabel === 'Booking Status' && (
          <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Typography
              variant='h6'
              fontWeight='bold'
              color='primary'
              gutterBottom
            >
              Booking Status Management
            </Typography>
            <Divider sx={{ mb: 3 }} />
            {statusUpdateSuccess && (
              <Alert severity='success' sx={{ mb: 2 }}>
                {statusUpdateSuccess}
              </Alert>
            )}
            {statusUpdateError && (
              <Alert severity='error' sx={{ mb: 2 }}>
                {statusUpdateError}
              </Alert>
            )}
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Update Status</InputLabel>
                  <Select
                    value={statusUpdateForm.approvedStatus}
                    onChange={(e) =>
                      setStatusUpdateForm({
                        ...statusUpdateForm,
                        approvedStatus: e.target.value,
                      })
                    }
                    label='Update Status'
                    disabled={!isEditMode}
                  >
                    <MenuItem value='registry'>Registry</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label='Status Change Date'
                  type='date'
                  value={statusUpdateForm.date}
                  onChange={(e) =>
                    setStatusUpdateForm({
                      ...statusUpdateForm,
                      date: e.target.value,
                    })
                  }
                  InputLabelProps={{ shrink: true }}
                  disabled={!isEditMode}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label='Status Change Remark'
                  placeholder='Enter remark for status change...'
                  value={statusUpdateForm.remark}
                  onChange={(e) =>
                    setStatusUpdateForm({
                      ...statusUpdateForm,
                      remark: e.target.value,
                    })
                  }
                  disabled={!isEditMode}
                />
              </Grid>
              {isEditMode && (
                <Grid item xs={12}>
                  <Button
                    variant='contained'
                    disabled={statusUpdateSaving}
                    startIcon={
                      statusUpdateSaving ? (
                        <CircularProgress size={20} color='inherit' />
                      ) : (
                        <Save />
                      )
                    }
                    onClick={handleUpdateBookingStatus}
                  >
                    {statusUpdateSaving ? 'Updating...' : 'Update Status'}
                  </Button>
                </Grid>
              )}
            </Grid>
          </Paper>
        )}

        {/* Tab 7: Booking Draft */}
        {/* {
          activeTab === 7 && (
            <Paper sx={{ p: 3, borderRadius: 2 }}>
              <Typography variant="h6" fontWeight="bold" color="primary" gutterBottom>
                Booking Draft & Notes
              </Typography>
              <Divider sx={{ mb: 3 }} />
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Save your work as a draft to continue editing later
                  </Alert>
                  <TextField
                    fullWidth
                    multiline
                    rows={8}
                    label="Draft Notes"
                    placeholder="Enter any temporary notes or incomplete information here..."
                    defaultValue="Customer interested in Plot P105. Negotiating on final price. Follow up pending for additional discount request."
                    disabled={!isEditMode}
                  />
                </Grid>
                {isEditMode && (
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Button variant="outlined" startIcon={<Save />}>
                        Save as Draft
                      </Button>
                      <Button variant="outlined" color="warning">
                        Clear Draft
                      </Button>
                    </Box>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Paper sx={{ p: 3, bgcolor: '#f8f9fa' }}>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                      Draft History
                    </Typography>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="body2" color="text.secondary">
                      Last saved: 2025-01-20 10:30 AM by Admin
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Previous draft: 2025-01-19 03:45 PM by Admin
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </Paper>
          )
        } */}
      </Box>

      {/* Installment Status Dialog */}
      <Dialog
        open={statusDialogOpen}
        onClose={statusDialogSaving ? undefined : closeStatusDialog}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>Update Installment Status</DialogTitle>
        <DialogContent dividers>
          {statusDialogError && (
            <Alert severity='error' sx={{ mb: 2 }}>
              {statusDialogError}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label='Installment No.'
                value={selectedInstallment?.srNo || ''}
                disabled
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label='Due Date'
                value={
                  selectedInstallment?.dueDate
                    ? typeof selectedInstallment.dueDate === 'string'
                      ? selectedInstallment.dueDate
                      : new Date(selectedInstallment.dueDate)
                        .toISOString()
                        .split('T')[0]
                    : ''
                }
                disabled
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label='Amount'
                value={
                  selectedInstallment
                    ? `₹${(selectedInstallment.amount ?? 0).toLocaleString('en-IN')}`
                    : ''
                }
                disabled
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  label='Status'
                  value={statusForm.status}
                  onChange={(e) =>
                    handleStatusFormChange('status', e.target.value)
                  }
                >
                  <MenuItem value='PENDING'>PENDING</MenuItem>
                  <MenuItem value='PARTIALLY_PAID'>PARTIALLY_PAID</MenuItem>
                  <MenuItem value='PAID'>PAID</MenuItem>
                  <MenuItem value='OVERDUE'>OVERDUE</MenuItem>
                  <MenuItem value='CANCELLED'>CANCELLED</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label='Paid Date'
                type='date'
                value={statusForm.paidDate}
                onChange={(e) =>
                  handleStatusFormChange('paidDate', e.target.value)
                }
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label='Remark'
                multiline
                rows={3}
                value={statusForm.remark}
                onChange={(e) =>
                  handleStatusFormChange('remark', e.target.value)
                }
                placeholder='Add any remark for this installment update'
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeStatusDialog} disabled={statusDialogSaving}>
            Cancel
          </Button>
          <Button
            variant='contained'
            onClick={handleSaveInstallmentStatus}
            disabled={statusDialogSaving}
          >
            {statusDialogSaving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Receipt Confirmation Dialog */}
      <Dialog
        open={deleteReceiptDialogOpen}
        onClose={deletingReceiptId ? undefined : handleCancelDeleteReceipt}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CancelIcon color='error' />
          Cancel Receipt
        </DialogTitle>
        <DialogContent>
          <Typography variant='body1' sx={{ mb: 2 }}>
            Are you sure you want to cancel this receipt? This action cannot be
            undone.
          </Typography>
          {receiptToDelete && (
            <Box sx={{ bgcolor: '#f5f5f5', p: 2, borderRadius: 1, mb: 2 }}>
              <Typography variant='body2' color='text.secondary'>
                <strong>Receipt No:</strong> {receiptToDelete.receiptId || '-'}
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                <strong>Date:</strong>{' '}
                {receiptToDelete.receivedDate
                  ? new Date(receiptToDelete.receivedDate).toLocaleDateString(
                    'en-IN',
                  )
                  : '-'}
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                <strong>Amount:</strong> ₹
                {(receiptToDelete.amount || 0).toLocaleString('en-IN')}
              </Typography>
            </Box>
          )}
          <TextField
            fullWidth
            label='Cancellation Remark'
            placeholder='Enter reason for cancellation'
            multiline
            rows={3}
            value={cancelRemark}
            onChange={(e) => setCancelRemark(e.target.value)}
            sx={{ mt: 1 }}
          />
          <Typography variant='body2' color='error'>
            This action cannot be undone.
          </Typography>
          {receiptsError && (
            <Alert severity='error' sx={{ mt: 2 }}>
              {receiptsError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCancelDeleteReceipt}
            disabled={deletingReceiptId !== null}
          >
            Cancel
          </Button>
          <Button
            variant='contained'
            color='error'
            onClick={handleConfirmDeleteReceipt}
            disabled={deletingReceiptId !== null || !cancelRemark.trim()}
            startIcon={
              deletingReceiptId !== null ? (
                <CircularProgress size={20} color='inherit' />
              ) : (
                <CancelIcon />
              )
            }
          >
            {deletingReceiptId !== null ? 'Cancelling...' : 'Confirm Cancel'}
          </Button>
        </DialogActions>
      </Dialog>

      <BookingDetailsPDF
        open={pdfDialogOpen}
        onClose={() => setPdfDialogOpen(false)}
        type={pdfType}
        data={selectedReceipt}
        extraData={booking}
      />
    </Box>
  );
};

export default BookingDetails;
